import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { CR_ROLES, canBypassAcademicGate } from "@/lib/scannerPermissions";
import { resolveScannedQr } from "@/lib/resolveScannedQr";
import { getCrAllowedClasses, isCrOnly, normaliseClass } from "@/lib/crClassMapping";
import { getHodAllowedClasses, isHodOnly } from "@/lib/hodClassMapping";
import { useSearchParams } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { format, formatDistanceToNow } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { getSignedFileUrl } from "@/lib/getSignedFileUrl";
import { escapeHtml } from "@/lib/escapeHtml";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AppLayout } from "@/components/layout/AppLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import {
  Plus, Play, Square, ScanLine, Camera, CameraOff,
  CheckCircle2, XCircle, DoorOpen, Users, CalendarCheck, BarChart3, ShieldAlert,
  CalendarIcon, ClipboardList, AlertTriangle, Pencil, Trash2, FileSpreadsheet, Upload, UserPlus, RefreshCw, FileDown, Power, MapPin,
  Radio, ShieldCheck, BookOpen, GraduationCap,
} from "lucide-react";
import jsPDF from "jspdf";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useQrVisibility } from "@/hooks/useQrVisibility";
import { AttendanceReports } from "@/components/attendance/AttendanceReports";
import { SessionBulkAttendanceDialog } from "@/components/attendance/SessionBulkAttendanceDialog";
import { SessionsBulkCreateDialog } from "@/components/attendance/SessionsBulkCreateDialog";
import { PendingApprovalsQueue } from "@/components/attendance/PendingApprovalsQueue";
import { AddCrAttendanceDialog } from "@/components/attendance/AddCrAttendanceDialog";
import { SessionPrintableSheet } from "@/components/attendance/SessionPrintableSheet";
import { ReopenSessionGuard } from "@/components/attendance/ReopenSessionGuard";
import { RemedialSessionDialog } from "@/components/attendance/RemedialSessionDialog";
import { SessionRequestApprovals } from "@/components/attendance/SessionRequestApprovals";
import { SessionHistoryView } from "@/components/attendance/SessionHistoryView";
import { fuzzyScore } from "@/lib/fuzzyScore";
import { RotationTab } from "@/components/attendance/tuition/RotationTab";

/* ------------------------------------------------------------------ */
/*  Session summary PDF export                                         */
/* ------------------------------------------------------------------ */
function exportSummaryPdf(session: any, sum: any) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const margin = 40;
  let y = margin;

  // Header
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("ST. DAVID COLLEGE OF HEALTH SCIENCES", margin, y);
  y += 18;
  doc.setFontSize(11);
  doc.setTextColor(227, 131, 58); // brand orange
  doc.text("SESSION ATTENDANCE SUMMARY", margin, y);
  doc.setTextColor(0, 0, 0);
  y += 10;
  doc.setDrawColor(227, 131, 58);
  doc.setLineWidth(1.5);
  doc.line(margin, y, 555, y);
  y += 20;

  // Session meta
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const meta: [string, string][] = [
    ["Topic", session.topic || "Untitled"],
    ["Module", session.module_code || "—"],
    ["Class", session.class || "—"],
    ["Session date", session.session_date ? new Date(session.session_date).toLocaleDateString() : "—"],
    ["Closed at", sum.closed_at ? new Date(sum.closed_at).toLocaleString() : "—"],
  ];
  meta.forEach(([k, v]) => {
    doc.setFont("helvetica", "bold");
    doc.text(`${k}:`, margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(String(v), margin + 90, y);
    y += 16;
  });

  y += 10;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Summary", margin, y);
  y += 6;
  doc.setLineWidth(0.5);
  doc.setDrawColor(180, 180, 180);
  doc.line(margin, y, 555, y);
  y += 16;

  const ratePct = sum.attendance_rate != null
    ? `${(Number(sum.attendance_rate) * 100).toFixed(1)}%`
    : "—";

  const rows: [string, string | number][] = [
    ["Total enrolled", sum.total_enrolled ?? 0],
    ["Present", sum.present_count ?? 0],
    ["Pending (total)", sum.pending_count ?? 0],
    ["  ↳ Awaiting Academic Officer", sum.pending_approval_count ?? 0],
    ["  ↳ Awaiting HOD confirmation", sum.pending_hod_confirm_count ?? 0],
    ["Rejected", sum.rejected_count ?? 0],
    ["Absent", sum.absent_count ?? 0],
    ["Attendance rate", ratePct],
    ["CR-recorded", sum.cr_recorded_count ?? 0],
  ];
  doc.setFontSize(10);
  rows.forEach(([k, v]) => {
    doc.setFont("helvetica", "normal");
    doc.text(k, margin, y);
    doc.setFont("helvetica", "bold");
    doc.text(String(v), margin + 200, y);
    y += 16;
  });

  y += 14;
  doc.setFont("helvetica", "italic");
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text(
    `Generated ${sum.generated_at ? new Date(sum.generated_at).toLocaleString() : ""} • Exported ${new Date().toLocaleString()}`,
    margin,
    y
  );

  const safeTopic = (session.topic || "session").replace(/[^a-z0-9]+/gi, "_").slice(0, 40);
  const safeDate = session.session_date || "";
  doc.save(`session-summary-${safeTopic}-${safeDate}.pdf`);
}

/* Compact relative-time formatter for "last scan" indicators. */
function formatRelativeShort(iso: string | null | undefined, _nowTick: number): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 10) return "just now";
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return new Date(iso).toLocaleDateString();
}

/* ------------------------------------------------------------------ */
/*  Sessions Tab                                                       */
/* ------------------------------------------------------------------ */
function SessionsTab() {
  const [sessions, setSessions] = useState<any[]>([]);
  const [approvedCountBySession, setApprovedCountBySession] = useState<Record<string, number>>({});
  const [summariesBySession, setSummariesBySession] = useState<Record<string, any>>({});
  const [lastScanBySession, setLastScanBySession] = useState<Record<string, { lastScanAt: string | null; scanCount: number }>>({});
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [allModules, setAllModules] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [selectedSemester, setSelectedSemester] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedModule, setSelectedModule] = useState("");
  const [topic, setTopic] = useState("");
  const [customSessionNo, setCustomSessionNo] = useState("");
  const [customSessionName, setCustomSessionName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const CUSTOM_TOPIC = "__CUSTOM__";
  // Add Sessions dialog — library picker (mirrors Start dialog, but keyed on the form's selections)
  const [addLibrarySessions, setAddLibrarySessions] = useState<any[]>([]);
  const [addLibrarySessionId, setAddLibrarySessionId] = useState<string>("");
  const [addLibrarySearch, setAddLibrarySearch] = useState("");
  const [addKindFilter, setAddKindFilter] = useState<string>("all");
  const [editLibraryLocked, setEditLibraryLocked] = useState(false);
  const [bulkSession, setBulkSession] = useState<any | null>(null);
  const [bulkCreateOpen, setBulkCreateOpen] = useState(false);
  const [addCrSession, setAddCrSession] = useState<any | null>(null);
  // Start Session picker state — sources from module_sessions (the catalogue)
  const [startOpen, setStartOpen] = useState(false);
  const [startSemester, setStartSemester] = useState("");
  const [startClass, setStartClass] = useState("");
  const [startModuleCode, setStartModuleCode] = useState("");
  const [startLibrarySessionId, setStartLibrarySessionId] = useState(""); // module_sessions.id
  const [librarySessions, setLibrarySessions] = useState<any[]>([]);
  const [librarySearch, setLibrarySearch] = useState("");
  const [startKindFilter, setStartKindFilter] = useState<string>("all");
  const [starting, setStarting] = useState(false);
  const [confirmStartOpen, setConfirmStartOpen] = useState(false);
  const startInFlightRef = useRef(false);
  const [cancelReasonOpen, setCancelReasonOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [pendingCancelContext, setPendingCancelContext] = useState<{ libraryId: string; topic: string; moduleCode: string; className: string } | null>(null);

  async function downloadSessionsTemplate() {
    if (!selectedSemester || !selectedClass || !selectedModule) return;
    const mod = allModules.find((m) => m.module_code === selectedModule);
    const XLSX = await import("xlsx");
    const rows = Array.from({ length: 15 }, (_, i) => ({ "Session No": i + 1, "Session Name": "" }));
    const ws = XLSX.utils.json_to_sheet(rows, { header: ["Session No", "Session Name"] });
    ws["!cols"] = [{ wch: 12 }, { wch: 60 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sessions");
    const meta = XLSX.utils.aoa_to_sheet([
      ["Semester", selectedSemester],
      ["Class", selectedClass],
      ["Module Code", selectedModule],
      ["Module Name", mod?.module_name ?? ""],
      ["Generated At", new Date().toISOString()],
    ]);
    XLSX.utils.book_append_sheet(wb, meta, "Metadata");
    const filename = `Sessions_${selectedClass}_${selectedModule}_Sem${selectedSemester}.xlsx`;
    XLSX.writeFile(wb, filename);
    toast({ title: "Template downloaded", description: filename });
  }

  // Build a normalized, semester-aware row set for every stored session by
  // joining attendance_sessions to modules via module_code. This guarantees
  // the export reflects the same Semester → Class → Module relationship used
  // across the Attendance pickers (so Semester II sessions show up correctly).
  function buildSessionExportRows() {
    const modByCode = new Map(allModules.map((m: any) => [m.module_code, m]));
    const rows = sessions.map((s: any) => {
      const mod: any = s.module_code ? modByCode.get(s.module_code) : null;
      return {
        semester: mod?.semester ?? "—",
        class: mod?.class ?? s.class ?? "—",
        module_code: s.module_code ?? "—",
        module_name: mod?.module_name ?? "—",
        topic: s.topic ?? "",
        session_date: s.session_date ?? "",
        status: sessionStatusLabel(s.status),
        opened_at: s.opened_at ? new Date(s.opened_at).toLocaleString() : "",
        closed_at: s.closed_at ? new Date(s.closed_at).toLocaleString() : "",
      };
    });
    rows.sort((a, b) =>
      (a.semester || "").localeCompare(b.semester || "") ||
      (a.class || "").localeCompare(b.class || "") ||
      (a.module_code || "").localeCompare(b.module_code || "") ||
      (a.session_date || "").localeCompare(b.session_date || "")
    );
    return rows;
  }

  function exportSessionsCsv() {
    const rows = buildSessionExportRows();
    if (!rows.length) {
      toast({ title: "No sessions to export", variant: "destructive" });
      return;
    }
    const headers = ["Semester", "Class", "Module Code", "Module Name", "Topic", "Date", "Status", "Opened", "Closed"];
    const escape = (v: any) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.join(",")];
    for (const r of rows) {
      lines.push([r.semester, r.class, r.module_code, r.module_name, r.topic, r.session_date, r.status, r.opened_at, r.closed_at].map(escape).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Sessions_All_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "CSV exported", description: `${rows.length} sessions` });
  }

  function exportSessionsPdf() {
    const rows = buildSessionExportRows();
    if (!rows.length) {
      toast({ title: "No sessions to export", variant: "destructive" });
      return;
    }
    const bySemester = rows.reduce<Record<string, typeof rows>>((acc, r) => {
      (acc[r.semester] ||= []).push(r);
      return acc;
    }, {});
    const semesterKeys = Object.keys(bySemester).sort();
    const win = window.open("", "_blank");
    if (!win) {
      toast({ title: "Popup blocked", description: "Allow popups to export PDF", variant: "destructive" });
      return;
    }
    const sectionHtml = semesterKeys.map((sem) => {
      const list = bySemester[sem];
      const body = list.map((r) => `
        <tr><td>${escapeHtml(r.class)}</td><td>${escapeHtml(r.module_code)}</td><td>${escapeHtml(r.module_name)}</td><td>${escapeHtml(r.topic)}</td><td>${escapeHtml(r.session_date)}</td><td>${escapeHtml(r.status)}</td></tr>`).join("");
      return `<h2>Semester ${escapeHtml(sem)} <span class="count">(${list.length})</span></h2>
        <table><thead><tr><th>Class</th><th>Module</th><th>Module Name</th><th>Topic</th><th>Date</th><th>Status</th></tr></thead><tbody>${body}</tbody></table>`;
    }).join("");
    win.document.write(`<!doctype html><html><head><title>Sessions Export</title>
      <style>
        body { font-family: system-ui, sans-serif; padding: 16px; color: #111; }
        h1 { font-size: 18px; margin: 0 0 4px; }
        .meta { font-size: 11px; color: #555; margin-bottom: 14px; }
        h2 { font-size: 14px; margin: 18px 0 6px; border-bottom: 1px solid #ddd; padding-bottom: 3px; }
        h2 .count { color: #666; font-weight: normal; font-size: 12px; }
        table { width: 100%; border-collapse: collapse; font-size: 10.5px; }
        th, td { border: 1px solid #ccc; padding: 4px 6px; text-align: left; vertical-align: top; }
        th { background: #f3f4f6; }
        @media print { body { padding: 0; } }
      </style></head><body>
      <h1>All Attendance Sessions</h1>
      <div class="meta">Generated ${new Date().toLocaleString()} · ${rows.length} sessions across ${semesterKeys.length} semester(s)</div>
      ${sectionHtml}
      <script>window.onload = () => { window.print(); };</script>
      </body></html>`);
    win.document.close();
    toast({ title: "PDF prepared", description: `${rows.length} sessions` });
  }
  const { toast } = useToast();
  const { user, hasRole } = useAuth();
  const canManage = hasRole("superadmin") || hasRole("it_incharge");
  const canManageSession = hasRole("superadmin") || hasRole("head_administration") || hasRole("head_academic") || hasRole("it_incharge");

  async function downloadTemplate(s: any) {
    if (!s.class) {
      toast({ title: "Session has no class assigned", variant: "destructive" });
      return;
    }
    const { data: students, error } = await supabase
      .from("students")
      .select("first_name, last_name, student_number, form_iv_index")
      .eq("class", s.class)
      .eq("status", "active")
      .order("first_name");
    if (error) {
      toast({ title: "Error loading students", description: error.message, variant: "destructive" });
      return;
    }
    if (!students?.length) {
      toast({ title: "No active students for this class", variant: "destructive" });
      return;
    }
    const XLSX = await import("xlsx");
    const headers = ["SN", "Student ID", "Student Name", "Form IV Index", "Status"];
    const rows = students.map((st, i) => ({
      SN: i + 1,
      "Student ID": st.student_number ?? "",
      "Student Name": `${st.first_name} ${st.last_name}`,
      "Form IV Index": st.form_iv_index ?? "",
      Status: "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
    ws["!cols"] = [{ wch: 5 }, { wch: 22 }, { wch: 28 }, { wch: 22 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Attendance");
    const meta = XLSX.utils.aoa_to_sheet([
      ["Field", "Value"],
      ["Session ID", s.id],
      ["Date", s.session_date],
      ["Module", s.module_code ?? ""],
      ["Class", s.class ?? ""],
      ["Topic", s.topic ?? ""],
      ["Total Students", String(students.length)],
      ["Valid Status Values", "present, absent, late, excused"],
    ]);
    XLSX.utils.book_append_sheet(wb, meta, "Metadata");
    const filename = `Attendance_${s.module_code ?? "session"}_${s.class ?? ""}_${s.session_date}.xlsx`;
    XLSX.writeFile(wb, filename);
    toast({ title: "Template downloaded", description: filename });
  }

  const load = useCallback(async () => {
    const [s, m] = await Promise.all([
      supabase.from("attendance_sessions").select("*").neq("status", "closed").order("created_at", { ascending: false }),
      supabase.from("modules").select("module_code, module_name, class, semester").order("module_code"),
    ]);
    setSessions(s.data ?? []);
    setAllModules(m.data ?? []);

    // Build approved-count map for "Add CR Attendance" gating
    const sessionIds = (s.data ?? []).map((row: any) => row.id);
    const closedIds = (s.data ?? []).filter((row: any) => row.status === "closed").map((row: any) => row.id);
    if (sessionIds.length) {
      const { data: appr } = await supabase
        .from("attendance_records")
        .select("session_id, scanned_at, status")
        .in("session_id", sessionIds)
        .eq("attendance_type", "session");
      const counts: Record<string, number> = {};
      const liveMap: Record<string, { lastScanAt: string | null; scanCount: number }> = {};
      (appr ?? []).forEach((r: any) => {
        if (!r.session_id) return;
        if (r.status === "present") {
          counts[r.session_id] = (counts[r.session_id] ?? 0) + 1;
        }
        const cur = liveMap[r.session_id] ?? { lastScanAt: null, scanCount: 0 };
        cur.scanCount += 1;
        if (r.scanned_at && (!cur.lastScanAt || new Date(r.scanned_at) > new Date(cur.lastScanAt))) {
          cur.lastScanAt = r.scanned_at;
        }
        liveMap[r.session_id] = cur;
      });
      setApprovedCountBySession(counts);
      setLastScanBySession(liveMap);
    } else {
      setApprovedCountBySession({});
      setLastScanBySession({});
    }

    // Fetch frozen summaries for closed sessions
    if (closedIds.length) {
      const { data: sums } = await supabase
        .from("session_summaries")
        .select("*")
        .in("session_id", closedIds);
      const map: Record<string, any> = {};
      (sums ?? []).forEach((row: any) => { map[row.session_id] = row; });
      setSummariesBySession(map);
    } else {
      setSummariesBySession({});
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live refresh: poll every 20s, refresh on tab visibility, and tick "now" every 30s for relative timestamps.
  useEffect(() => {
    const interval = setInterval(() => { load(); }, 20000);
    const tickInterval = setInterval(() => setNowTick(Date.now()), 30000);
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(interval);
      clearInterval(tickInterval);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [load]);

  // Realtime: bump lastScanAt when a new attendance_record arrives for an active session.
  useEffect(() => {
    const activeIds = sessions.map(s => s.id);
    if (!activeIds.length) return;
    const channel = supabase
      .channel("active-session-scans")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "attendance_records" },
        (payload: any) => {
          const row = payload?.new;
          if (!row?.session_id || !activeIds.includes(row.session_id)) return;
          setLastScanBySession(prev => {
            const cur = prev[row.session_id] ?? { lastScanAt: null, scanCount: 0 };
            return {
              ...prev,
              [row.session_id]: {
                lastScanAt: row.scanned_at ?? new Date().toISOString(),
                scanCount: cur.scanCount + 1,
              },
            };
          });
        },
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [sessions]);

  // Distinct semesters
  const semesters = [...new Set(allModules.map(m => m.semester))].sort();

  // Classes filtered by selected semester
  const filteredClasses = [...new Set(
    allModules.filter(m => m.semester === selectedSemester).map(m => m.class)
  )].sort();

  // Modules filtered by semester + class
  const filteredModules = allModules.filter(
    m => m.semester === selectedSemester && m.class === selectedClass
  );

  function handleSemesterChange(val: string) {
    setSelectedSemester(val);
    setSelectedClass("");
    setSelectedModule("");
  }

  function handleClassChange(val: string) {
    setSelectedClass(val);
    setSelectedModule("");
  }

  async function regenerateSummary(sessionId: string) {
    const { error } = await supabase.rpc("generate_session_summary", { _session_id: sessionId });
    if (error) {
      toast({ title: "Failed to regenerate", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Summary regenerated" });
    await load();
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    const mod = allModules.find(m => m.module_code === selectedModule);
    // Enforce that the chosen module exists and matches the selected semester so
    // the saved module_code resolves to the correct semester via modules.semester.
    if (!mod) {
      toast({ title: "Invalid module", description: "Select a module from the list.", variant: "destructive" });
      return;
    }
    if (selectedSemester && mod.semester !== selectedSemester) {
      toast({
        title: "Semester mismatch",
        description: `Module ${mod.module_code} belongs to Semester ${mod.semester}, not ${selectedSemester}.`,
        variant: "destructive",
      });
      return;
    }
    if (selectedClass && mod.class !== selectedClass) {
      toast({
        title: "Class mismatch",
        description: `Module ${mod.module_code} belongs to ${mod.class}, not ${selectedClass}.`,
        variant: "destructive",
      });
      return;
    }
    const isCustom = topic === CUSTOM_TOPIC;
    const isLibrary = !isCustom && !!addLibrarySessionId;
    let finalTopic = topic;
    let libraryId: string | null = null;
    if (isCustom) {
      const no = customSessionNo.trim();
      const name = customSessionName.trim();
      if (!no || !name) {
        toast({ title: "Missing fields", description: "Enter both Session No and Session Name.", variant: "destructive" });
        return;
      }
      finalTopic = `Session ${no}: ${name}`;
    } else if (isLibrary) {
      const lib = addLibrarySessions.find((x) => x.id === addLibrarySessionId);
      if (!lib) {
        toast({ title: "Pick a session", description: "Choose a session from the library.", variant: "destructive" });
        return;
      }
      finalTopic = `Session ${lib.session_no}: ${lib.session_name}`;
      libraryId = lib.id;
    } else {
      toast({ title: "Pick a session", description: "Choose a session from the library or use Custom.", variant: "destructive" });
      return;
    }
    if (editingId) {
      const updates: { topic: string; module_code: string; class: string; excluded_from_coverage: boolean; module_session_id?: string | null } = {
        topic: finalTopic,
        module_code: mod.module_code,
        class: mod.class,
        excluded_from_coverage: false,
      };
      if (!editLibraryLocked) updates.module_session_id = libraryId;
      const { error } = await supabase.from("attendance_sessions").update(updates).eq("id", editingId);
      if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
      else {
        toast({ title: "Session updated" });
        resetForm();
        load();
      }
      return;
    }
    const { error } = await supabase.from("attendance_sessions").insert({
      topic: finalTopic,
      module_code: mod.module_code,
      class: mod.class,
      created_by: user?.id,
      excluded_from_coverage: false,
      module_session_id: libraryId,
      status: "idle",
    });
    if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
    else {
      toast({ title: "Session created", description: "Click Activate when class begins to allow scanning." });
      resetForm();
      load();
    }
  }

  function resetForm() {
    setOpen(false);
    setEditingId(null);
    setSelectedSemester("");
    setSelectedClass("");
    setSelectedModule("");
    setTopic("");
    setCustomSessionNo("");
    setCustomSessionName("");
    setAddLibrarySessionId("");
    setAddLibrarySearch("");
    setAddKindFilter("all");
    setEditLibraryLocked(false);
  }

  function startEdit(s: any) {
    const mod = allModules.find(m => m.module_code === s.module_code);
    setEditingId(s.id);
    setSelectedSemester(mod?.semester ?? "");
    setSelectedClass(s.class ?? mod?.class ?? "");
    setSelectedModule(s.module_code ?? "");
    const existingTopic = s.topic ?? "";
    const customMatch = existingTopic.match(/^Session\s+(\S+):\s*(.+)$/);
    setAddLibrarySearch("");
    setAddKindFilter("all");
    if (s.module_session_id) {
      // Library-mapped session — preselect and lock until user clicks "Change"
      setAddLibrarySessionId(s.module_session_id);
      setEditLibraryLocked(true);
      setTopic("");
      setCustomSessionNo("");
      setCustomSessionName("");
    } else if (customMatch) {
      setEditLibraryLocked(false);
      setAddLibrarySessionId("");
      setTopic(CUSTOM_TOPIC);
      setCustomSessionNo(customMatch[1]);
      setCustomSessionName(customMatch[2]);
    } else {
      setEditLibraryLocked(false);
      setAddLibrarySessionId("");
      setTopic(CUSTOM_TOPIC);
      setCustomSessionNo("");
      setCustomSessionName(existingTopic);
    }
    setOpen(true);
  }

  async function deleteSession(id: string) {
    const { error } = await supabase.from("attendance_sessions").delete().eq("id", id);
    if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
    else {
      toast({ title: "Session deleted" });
      load();
    }
  }

  async function toggleSession(id: string, current: string) {
    const newStatus = current === "open" ? "closed" : "open";
    const updates: { status: string; opened_at?: string; closed_at?: string | null } = { status: newStatus };
    if (newStatus === "open") updates.opened_at = new Date().toISOString();
    if (newStatus === "closed") updates.closed_at = new Date().toISOString();

    // Capture prior state for audit when reopening a closed session
    let priorClosedAt: string | null = null;
    if (current === "closed" && newStatus === "open") {
      const { data: prior } = await supabase
        .from("attendance_sessions")
        .select("closed_at")
        .eq("id", id)
        .maybeSingle();
      priorClosedAt = prior?.closed_at ?? null;
      // Clear closed_at on reopen so it reflects only the latest closure
      updates.closed_at = null;
    }

    const { error } = await supabase.from("attendance_sessions").update(updates).eq("id", id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    if (newStatus === "closed") {
      const closedAt = updates.closed_at ?? new Date().toISOString();
      // Record who closed the session for accountability — mirrors session_reopened auditing.
      await supabase.from("audit_logs").insert({
        user_id: user?.id ?? null,
        action: "session_closed",
        table_name: "attendance_sessions",
        record_id: id,
        old_data: { status: "open" },
        new_data: { status: "closed", closed_at: closedAt, closed_by: user?.id ?? null },
      });
      const { error: sumErr } = await supabase.rpc("generate_session_summary", { _session_id: id });
      if (sumErr) {
        toast({ title: "Summary not generated", description: sumErr.message, variant: "destructive" });
      } else {
        toast({ title: "Session closed", description: "Final attendance summary generated. Closure recorded." });
      }
    }
    if (current === "closed" && newStatus === "open") {
      // Audit the reopen action so management can review who reopened and when
      await supabase.from("audit_logs").insert({
        user_id: user?.id ?? null,
        action: "session_reopened",
        table_name: "attendance_sessions",
        record_id: id,
        old_data: { status: "closed", closed_at: priorClosedAt },
        new_data: { status: "open", reopened_at: new Date().toISOString(), reopened_by: user?.id ?? null },
      });
      toast({ title: "Session reopened", description: "Scanning is enabled again. The reopen has been logged." });
    }
    load();
  }

  // Activate an idle/scheduled session: flips it to 'open' and stamps opened_at.
  async function activateSession(id: string) {
    const openedAt = new Date().toISOString();
    const { error } = await supabase
      .from("attendance_sessions")
      .update({ status: "open", opened_at: openedAt })
      .eq("id", id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    await supabase.from("audit_logs").insert({
      user_id: user?.id ?? null,
      action: "session_activated",
      table_name: "attendance_sessions",
      record_id: id,
      new_data: { status: "open", opened_at: openedAt, activated_by: user?.id ?? null },
    });
    toast({ title: "Session activated", description: "Scanning is now enabled." });
    load();
  }

  // Centralized session status presentation. DB values: 'idle' | 'open' | 'closed' (legacy: 'scheduled').
  const sessionStatusLabel = (s: string) =>
    s === "open" ? "Active" : s === "closed" ? "Closed" : "Not active";
  const sessionStatusVariant = (s: string): "default" | "secondary" | "outline" =>
    s === "open" ? "default" : s === "closed" ? "outline" : "secondary";

  // "Idle" = anything that isn't open or closed (covers 'idle' and any legacy 'scheduled' rows).
  const isIdleStatus = (s: string) => s !== "open" && s !== "closed";

  // Start Session cascade — sources options from module_sessions catalogue.
  const startSemesterOptions = [...new Set(allModules.map((m) => m.semester).filter(Boolean) as string[])].sort();
  const startClassOptions = [...new Set(
    allModules.filter((m) => m.semester === startSemester).map((m) => m.class).filter(Boolean) as string[],
  )].sort();
  const startModuleOptions = [...new Set(
    allModules
      .filter((m) => m.semester === startSemester && m.class === startClass)
      .map((m) => m.module_code)
      .filter(Boolean) as string[],
  )].sort();
  const moduleNameOf = (code: string) =>
    allModules.find((m) => m.module_code === code)?.module_name ?? "";

  // Load catalogue when semester+class+module are picked.
  useEffect(() => {
    if (!startOpen || !startSemester || !startClass || !startModuleCode) {
      setLibrarySessions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("module_sessions")
        .select("id, session_no, session_name, session_kind")
        .eq("semester", startSemester)
        .eq("class", startClass)
        .eq("module_code", startModuleCode)
        .eq("active", true)
        .order("session_no", { ascending: true });
      if (cancelled) return;
      if (error) {
        toast({ title: "Could not load session library", description: error.message, variant: "destructive" });
        setLibrarySessions([]);
      } else {
        setLibrarySessions(data ?? []);
      }
    })();
    return () => { cancelled = true; };
  }, [startOpen, startSemester, startClass, startModuleCode, toast]);

  // Add Sessions dialog — load library entries for the chosen Semester/Class/Module.
  useEffect(() => {
    if (!open || !selectedSemester || !selectedClass || !selectedModule) {
      setAddLibrarySessions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("module_sessions")
        .select("id, session_no, session_name, session_kind")
        .eq("semester", selectedSemester)
        .eq("class", selectedClass)
        .eq("module_code", selectedModule)
        .eq("active", true)
        .order("session_no", { ascending: true });
      if (cancelled) return;
      if (error) {
        toast({ title: "Could not load session library", description: error.message, variant: "destructive" });
        setAddLibrarySessions([]);
      } else {
        setAddLibrarySessions(data ?? []);
      }
    })();
    return () => { cancelled = true; };
  }, [open, selectedSemester, selectedClass, selectedModule, toast]);

  const addKindOptions = useMemo(() => {
    const set = new Set<string>();
    addLibrarySessions.forEach((l) => l.session_kind && set.add(l.session_kind));
    return Array.from(set).sort();
  }, [addLibrarySessions]);

  const filteredAddLibrarySessions = useMemo(() => {
    const q = addLibrarySearch.trim();
    let pool = addLibrarySessions;
    if (addKindFilter !== "all") {
      pool = pool.filter((l) => l.session_kind === addKindFilter);
    }
    if (!q) return pool;
    const scored = pool
      .map((l) => {
        const haystack = `#${l.session_no} ${l.session_name} ${l.session_kind ?? ""}`;
        const score = fuzzyScore(haystack, q);
        return score == null ? null : { l, score };
      })
      .filter((x): x is { l: any; score: number } => x !== null)
      .sort((a, b) => a.score - b.score || a.l.session_no - b.l.session_no);
    return scored.map((x) => x.l);
  }, [addLibrarySessions, addLibrarySearch, addKindFilter]);

  // Sessions already created for this Semester/Class/Module — to disable already-used library entries.
  const usedLibraryIdsForAdd = useMemo(() => {
    const set = new Set<string>();
    for (const s of sessions) {
      if (s.module_code === selectedModule && s.class === selectedClass && s.module_session_id) {
        set.add(s.module_session_id);
      }
    }
    return set;
  }, [sessions, selectedModule, selectedClass]);


  // Map: catalogue id → currently-running runtime session (if any) for status badges.
  const runningByLibraryId = useMemo(() => {
    const map: Record<string, any> = {};
    for (const s of sessions) {
      if (s.module_session_id && s.status === "open") map[s.module_session_id] = s;
    }
    return map;
  }, [sessions]);

  // fuzzyScore moved to src/lib/fuzzyScore.ts and imported above for reuse.

  const startKindOptions = useMemo(() => {
    const set = new Set<string>();
    librarySessions.forEach((l) => l.session_kind && set.add(l.session_kind));
    return Array.from(set).sort();
  }, [librarySessions]);

  const filteredLibrarySessions = useMemo(() => {
    const q = librarySearch.trim();
    let pool = librarySessions;
    if (startKindFilter !== "all") {
      pool = pool.filter((l) => l.session_kind === startKindFilter);
    }
    if (!q) return pool;
    const scored = pool
      .map((l) => {
        const haystack = `#${l.session_no} ${l.session_name} ${l.session_kind ?? ""} ${l.module_code ?? ""} ${l.module_name ?? ""}`;
        const score = fuzzyScore(haystack, q);
        return score == null ? null : { l, score };
      })
      .filter((x): x is { l: any; score: number } => x !== null)
      .sort((a, b) => a.score - b.score || a.l.session_no - b.l.session_no);
    return scored.map((x) => x.l);
  }, [librarySessions, librarySearch, startKindFilter]);

  function resetStartPicker() {
    setStartSemester("");
    setStartClass("");
    setStartModuleCode("");
    setStartLibrarySessionId("");
    setLibrarySearch("");
    setStartKindFilter("all");
    setLibrarySessions([]);
  }

  function handleStartDialogChange(o: boolean) {
    if (o) {
      setStartOpen(true);
      return;
    }
    if (startLibrarySessionId && !starting) {
      const s = librarySessions.find((x) => x.id === startLibrarySessionId);
      setPendingCancelContext({
        libraryId: startLibrarySessionId,
        topic: s ? `Session ${s.session_no}: ${s.session_name}` : "Untitled",
        moduleCode: startModuleCode,
        className: startClass,
      });
      setCancelReason("");
      setCancelReasonOpen(true);
    }
    setStartOpen(false);
    resetStartPicker();
  }

  async function submitCancelReason(skip: boolean) {
    if (pendingCancelContext) {
      const reason = skip ? null : cancelReason.trim() || null;
      try {
        await supabase.from("audit_logs").insert({
          user_id: user?.id,
          action: "session_start_cancelled",
          table_name: "module_sessions",
          record_id: pendingCancelContext.libraryId,
          new_data: {
            reason,
            topic: pendingCancelContext.topic,
            module_code: pendingCancelContext.moduleCode,
            class: pendingCancelContext.className,
            cancelled_at: new Date().toISOString(),
          },
        });
        if (reason) {
          toast({ title: "Cancellation logged", description: "Reason recorded." });
        }
      } catch {
        /* non-blocking */
      }
    }
    setCancelReasonOpen(false);
    setCancelReason("");
    setPendingCancelContext(null);
  }

  // Pick a session from the library, then create + activate a runtime row in one go.
  // If the same library entry is already running, route to it instead.
  async function handleStartSession() {
    if (!startLibrarySessionId) return;
    if (startInFlightRef.current || starting) return;
    startInFlightRef.current = true;
    setStarting(true);
    setConfirmStartOpen(false);

    const libSession = librarySessions.find((x) => x.id === startLibrarySessionId);
    if (!libSession) {
      setStarting(false); startInFlightRef.current = false;
      toast({ title: "Session not found", variant: "destructive" });
      return;
    }
    const finalTopic = `Session ${libSession.session_no}: ${libSession.session_name}`;

    // Guard 1: already running for this catalogue entry?
    const { data: alreadyOpen } = await supabase
      .from("attendance_sessions")
      .select("id, topic")
      .eq("module_session_id", startLibrarySessionId)
      .eq("status", "open")
      .limit(1);
    if (alreadyOpen && alreadyOpen.length > 0) {
      setStarting(false); startInFlightRef.current = false;
      toast({
        title: "This session is already running",
        description: `"${alreadyOpen[0].topic}" is currently live. Open it from the workspace below.`,
      });
      setStartOpen(false);
      resetStartPicker();
      load();
      return;
    }

    // Guard 2: another open session for the same class + module
    const { data: existingOpen, error: checkError } = await supabase
      .from("attendance_sessions")
      .select("id, topic")
      .eq("status", "open")
      .eq("class", startClass)
      .eq("module_code", startModuleCode)
      .limit(1);
    if (checkError) {
      setStarting(false); startInFlightRef.current = false;
      toast({ title: "Could not verify open sessions", description: checkError.message, variant: "destructive" });
      return;
    }
    if (existingOpen && existingOpen.length > 0) {
      setStarting(false); startInFlightRef.current = false;
      toast({
        title: "Another session is already open",
        description: `"${existingOpen[0].topic || "Untitled"}" for ${startClass} • ${startModuleCode} is currently open. Close it before starting a new one.`,
        variant: "destructive",
      });
      load();
      return;
    }

    // Insert runtime row directly as 'open' so we get atomic create+activate.
    const startedAt = new Date().toISOString();
    const today = startedAt.slice(0, 10);
    const { data: inserted, error } = await supabase
      .from("attendance_sessions")
      .insert({
        topic: finalTopic,
        module_code: startModuleCode,
        class: startClass,
        session_date: today,
        status: "open",
        opened_at: startedAt,
        module_session_id: startLibrarySessionId,
        created_by: user?.id,
      })
      .select("id")
      .single();
    setStarting(false);
    startInFlightRef.current = false;
    if (error) {
      toast({ title: "Could not start session", description: error.message, variant: "destructive" });
      return;
    }
    try {
      await supabase.from("audit_logs").insert({
        user_id: user?.id,
        action: "session_started",
        table_name: "attendance_sessions",
        record_id: inserted?.id,
        new_data: {
          status: "open",
          opened_at: startedAt,
          started_by: user?.id,
          topic: finalTopic,
          module_code: startModuleCode,
          class: startClass,
          semester: startSemester,
          module_session_id: startLibrarySessionId,
        },
      });
    } catch { /* non-blocking */ }
    toast({ title: "Session started", description: "Scanning is now enabled." });
    setStartOpen(false);
    resetStartPicker();
    load();
  }

  return (
    <div className="space-y-4">
      <SessionRequestApprovals />
      <div className="flex justify-between items-start gap-2 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold">Sessions</h2>
          <p className="text-xs text-muted-foreground">Create sessions and activate them when class begins.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Dialog open={startOpen} onOpenChange={handleStartDialogChange}>
            <DialogTrigger asChild>
              <Button size="sm"><Play className="w-4 h-4 mr-1" />Start Session</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Start a Session</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Semester</Label>
                  <Select
                    value={startSemester}
                    onValueChange={(v) => { setStartSemester(v); setStartClass(""); setStartModuleCode(""); setStartLibrarySessionId(""); }}
                  >
                    <SelectTrigger><SelectValue placeholder="Select semester" /></SelectTrigger>
                    <SelectContent>
                      {startSemesterOptions.length === 0 ? (
                        <div className="px-2 py-2 text-sm text-muted-foreground">
                          No modules configured.
                        </div>
                      ) : startSemesterOptions.map((sem) => (
                        <SelectItem key={sem} value={sem}>Semester {sem}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {startSemester && (
                  <div className="space-y-2">
                    <Label>Class</Label>
                    <Select
                      value={startClass}
                      onValueChange={(v) => { setStartClass(v); setStartModuleCode(""); setStartLibrarySessionId(""); }}
                    >
                      <SelectTrigger><SelectValue placeholder="Select class" /></SelectTrigger>
                      <SelectContent>
                        {startClassOptions.length === 0 ? (
                          <div className="px-2 py-2 text-sm text-muted-foreground">
                            No classes for this semester.
                          </div>
                        ) : startClassOptions.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {startSemester && startClass && (
                  <div className="space-y-2">
                    <Label>Module</Label>
                    <Select
                      value={startModuleCode}
                      onValueChange={(v) => { setStartModuleCode(v); setStartLibrarySessionId(""); }}
                    >
                      <SelectTrigger><SelectValue placeholder="Select module" /></SelectTrigger>
                      <SelectContent>
                        {startModuleOptions.map((code) => (
                          <SelectItem key={code} value={code}>
                            {code}{moduleNameOf(code) ? ` — ${moduleNameOf(code)}` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {startSemester && startClass && startModuleCode && (
                  <div className="space-y-2">
                    <Label>Session</Label>
                    {librarySessions.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        No sessions in the library for this module yet. Add some under <strong>Academic → Session Library</strong>.
                      </p>
                    ) : (
                      <>
                        <Input
                          placeholder="Fuzzy search: number, name, kind, module…"
                          value={librarySearch}
                          onChange={(e) => setLibrarySearch(e.target.value)}
                          className="mb-2"
                        />
                        {startKindOptions.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1.5 mb-2">
                            <span className="text-xs text-muted-foreground mr-1">Kind:</span>
                            <Button
                              type="button"
                              size="sm"
                              variant={startKindFilter === "all" ? "default" : "outline"}
                              className="h-7 px-2.5 text-xs"
                              onClick={() => setStartKindFilter("all")}
                            >
                              All
                            </Button>
                            {startKindOptions.map((k) => (
                              <Button
                                key={k}
                                type="button"
                                size="sm"
                                variant={startKindFilter === k ? "default" : "outline"}
                                className="h-7 px-2.5 text-xs capitalize"
                                onClick={() => setStartKindFilter(k)}
                              >
                                {k.replace(/_/g, " ")}
                              </Button>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs text-muted-foreground">
                            {filteredLibrarySessions.length} of {librarySessions.length} session{librarySessions.length === 1 ? "" : "s"}
                          </p>
                          {(librarySearch || startKindFilter !== "all") && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs"
                              onClick={() => { setLibrarySearch(""); setStartKindFilter("all"); }}
                            >
                              Clear filters
                            </Button>
                          )}
                        </div>
                        <div className="max-h-64 overflow-y-auto border rounded divide-y">
                          {filteredLibrarySessions.map((s) => {
                            const running = runningByLibraryId[s.id];
                            const isSelected = startLibrarySessionId === s.id;
                            return (
                              <button
                                type="button"
                                key={s.id}
                                onClick={() => !running && setStartLibrarySessionId(s.id)}
                                disabled={!!running}
                                className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 transition ${
                                  isSelected ? "bg-accent" : "hover:bg-muted"
                                } ${running ? "opacity-60 cursor-not-allowed" : ""}`}
                              >
                                <div className="min-w-0">
                                  <div className="font-medium truncate">
                                    #{s.session_no} · {s.session_name}
                                  </div>
                                  <div className="text-xs text-muted-foreground capitalize">
                                    {(s.session_kind || "").replace(/_/g, " ")}
                                  </div>
                                </div>
                                {running ? (
                                  <Badge className="bg-green-100 text-green-700 border-transparent">Live</Badge>
                                ) : isSelected ? (
                                  <Badge>Selected</Badge>
                                ) : null}
                              </button>
                            );
                          })}
                          {filteredLibrarySessions.length === 0 && (
                            <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                              No matches. Try clearing filters.
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}

                <AlertDialog
                  open={confirmStartOpen}
                  onOpenChange={(o) => { if (!starting) setConfirmStartOpen(o); }}
                >
                  <AlertDialogTrigger asChild>
                    <Button
                      className="w-full"
                      disabled={!startLibrarySessionId || starting || confirmStartOpen}
                    >
                      <Play className="w-4 h-4 mr-1" />
                      {starting ? "Starting..." : confirmStartOpen ? "Confirm in dialog..." : "Start Session"}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Start this session now?</AlertDialogTitle>
                      <AlertDialogDescription>
                        {(() => {
                          const s = librarySessions.find((x) => x.id === startLibrarySessionId);
                          const topicLabel = s ? `Session ${s.session_no}: ${s.session_name}` : "Untitled";
                          const modName = moduleNameOf(startModuleCode);
                          const modLabel = modName ? `${startModuleCode} — ${modName}` : startModuleCode;
                          return `A new attendance session will be opened for "${topicLabel}" (${modLabel} — ${startClass}). Scanning will be enabled immediately.`;
                        })()}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel disabled={starting}>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        disabled={starting || startInFlightRef.current}
                        onClick={(e) => {
                          // Prevent Radix from auto-closing before our async work checks the guard
                          e.preventDefault();
                          handleStartSession();
                        }}
                      >
                        {starting ? "Starting..." : "Start Session"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={cancelReasonOpen} onOpenChange={(o) => { if (!o) submitCancelReason(true); }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Cancelled starting session</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                {pendingCancelContext && (
                  <p className="text-sm text-muted-foreground">
                    You did not start "{pendingCancelContext.topic}" ({pendingCancelContext.moduleCode} — {pendingCancelContext.className}). Optionally tell us why.
                  </p>
                )}
                <div className="space-y-2">
                  <Label>Reason (optional)</Label>
                  <Textarea
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="e.g. Wrong session selected, lecturer absent, schedule conflict..."
                    rows={3}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => submitCancelReason(true)}>Skip</Button>
                  <Button onClick={() => submitCancelReason(false)}>Save reason</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>
                <Plus className="w-4 h-4 mr-1" />Add Sessions
              </Button>
            </DialogTrigger>
            <DialogContent>
            <DialogHeader><DialogTitle>{editingId ? "Edit Session" : "Add Sessions"}</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              {/* Semester */}
              <div className="space-y-2">
                <Label>Semester</Label>
                <Select value={selectedSemester} onValueChange={handleSemesterChange}>
                  <SelectTrigger><SelectValue placeholder="Select semester" /></SelectTrigger>
                  <SelectContent>
                    {semesters.map(s => (
                      <SelectItem key={s} value={s}>Semester {s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Class — only if semester selected */}
              {selectedSemester && (
                <div className="space-y-2">
                  <Label>Class</Label>
                  <Select value={selectedClass} onValueChange={handleClassChange}>
                    <SelectTrigger><SelectValue placeholder="Select class" /></SelectTrigger>
                    <SelectContent>
                      {filteredClasses.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Module — only if class selected */}
              {selectedClass && (
                <div className="space-y-2">
                  <Label>Module</Label>
                  <Select value={selectedModule} onValueChange={setSelectedModule}>
                    <SelectTrigger><SelectValue placeholder="Select module" /></SelectTrigger>
                    <SelectContent>
                      {filteredModules.map(m => (
                        <SelectItem key={m.module_code} value={m.module_code}>
                          {m.module_code} — {m.module_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Library picker + bulk upload (Add mode) */}
              {selectedModule && !editingId && (
                <div className="grid gap-3 sm:grid-cols-2">
                  {/* New session card — drives from the Session Library catalogue */}
                  <div className="rounded-md border p-3 space-y-3 flex flex-col">
                    <div>
                      <p className="text-sm font-medium">Pick session from library</p>
                      <p className="text-xs text-muted-foreground">
                        Sessions pre-uploaded under Academic → Session Library for this module.
                      </p>
                    </div>

                    {addLibrarySessions.length === 0 ? (
                      <div className="flex-1 rounded border border-dashed p-3 text-xs text-muted-foreground space-y-2">
                        <p>
                          No sessions in the library for this module yet. Add some under{" "}
                          <strong>Academic → Session Library</strong>.
                        </p>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => { window.location.href = "/academic"; }}
                        >
                          Open Session Library
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2 flex-1">
                        <Input
                          value={addLibrarySearch}
                          onChange={(e) => setAddLibrarySearch(e.target.value)}
                          placeholder="Search session no/name…"
                          className="h-8 text-xs"
                        />
                        {addKindOptions.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            <button
                              type="button"
                              onClick={() => setAddKindFilter("all")}
                              className={`text-xs px-2 py-0.5 rounded border capitalize ${
                                addKindFilter === "all" ? "bg-accent" : "hover:bg-muted"
                              }`}
                            >
                              all
                            </button>
                            {addKindOptions.map((k) => (
                              <button
                                type="button"
                                key={k}
                                onClick={() => setAddKindFilter(k)}
                                className={`text-xs px-2 py-0.5 rounded border capitalize ${
                                  addKindFilter === k ? "bg-accent" : "hover:bg-muted"
                                }`}
                              >
                                {k.replace(/_/g, " ")}
                              </button>
                            ))}
                          </div>
                        )}
                        <div className="max-h-48 overflow-y-auto border rounded divide-y">
                          {filteredAddLibrarySessions.map((s) => {
                            const used = usedLibraryIdsForAdd.has(s.id);
                            const isSelected = addLibrarySessionId === s.id && topic !== CUSTOM_TOPIC;
                            return (
                              <button
                                type="button"
                                key={s.id}
                                disabled={used}
                                onClick={() => {
                                  setAddLibrarySessionId(s.id);
                                  setTopic("");
                                }}
                                className={`w-full text-left px-2 py-1.5 text-xs flex items-center justify-between gap-2 transition ${
                                  isSelected ? "bg-accent" : "hover:bg-muted"
                                } ${used ? "opacity-50 cursor-not-allowed" : ""}`}
                              >
                                <div className="min-w-0">
                                  <div className="font-medium truncate">
                                    #{s.session_no} · {s.session_name}
                                  </div>
                                  <div className="text-[10px] text-muted-foreground capitalize">
                                    {(s.session_kind || "").replace(/_/g, " ")}
                                  </div>
                                </div>
                                {used ? (
                                  <Badge variant="secondary" className="text-[10px]">Already added</Badge>
                                ) : isSelected ? (
                                  <Badge className="text-[10px]">Selected</Badge>
                                ) : null}
                              </button>
                            );
                          })}
                          {filteredAddLibrarySessions.length === 0 && (
                            <div className="px-2 py-3 text-xs text-muted-foreground text-center">
                              No matches.
                            </div>
                          )}
                        </div>

                        {/* Custom fallback */}
                        <button
                          type="button"
                          onClick={() => { setTopic(CUSTOM_TOPIC); setAddLibrarySessionId(""); }}
                          className={`w-full text-left text-xs px-2 py-1.5 rounded border transition ${
                            topic === CUSTOM_TOPIC ? "bg-accent" : "hover:bg-muted"
                          }`}
                        >
                          + Custom session (not in library)
                        </button>

                        {topic === CUSTOM_TOPIC && (
                          <div className="grid grid-cols-3 gap-2 pt-1">
                            <div className="space-y-1">
                              <Label className="text-xs">Session No</Label>
                              <Input
                                type="number"
                                min={1}
                                value={customSessionNo}
                                onChange={(e) => setCustomSessionNo(e.target.value)}
                                placeholder="e.g. 5"
                              />
                            </div>
                            <div className="space-y-1 col-span-2">
                              <Label className="text-xs">Session Name</Label>
                              <Input
                                value={customSessionName}
                                onChange={(e) => setCustomSessionName(e.target.value)}
                                placeholder="e.g. Cardiac arrhythmias"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <Button
                      type="submit"
                      size="sm"
                      disabled={
                        (topic !== CUSTOM_TOPIC && !addLibrarySessionId) ||
                        (topic === CUSTOM_TOPIC && (!customSessionNo.trim() || !customSessionName.trim()))
                      }
                    >
                      <Plus className="w-4 h-4 mr-1" /> Create Session
                    </Button>
                  </div>

                  {/* Bulk upload card */}
                  <div className="rounded-md border p-3 space-y-3 flex flex-col">
                    <div>
                      <p className="text-sm font-medium">Bulk upload sessions</p>
                      <p className="text-xs text-muted-foreground">Create many counted sessions from a template.</p>
                    </div>
                    <div className="flex-1" />
                    <div className="flex flex-col gap-2">
                      <Button type="button" size="sm" variant="outline" onClick={downloadSessionsTemplate}>
                        <FileSpreadsheet className="h-4 w-4 mr-1" /> Download Template
                      </Button>
                      <Button type="button" size="sm" variant="outline" onClick={() => setBulkCreateOpen(true)}>
                        <Upload className="h-4 w-4 mr-1" /> Upload .xlsx
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Edit mode — supports library-mapped (locked with Change link) or custom topic */}
              {editingId && selectedModule && (
                <>
                  <div className="space-y-2">
                    <Label>Session</Label>
                    {editLibraryLocked && addLibrarySessionId ? (
                      <div className="rounded border p-2 flex items-center justify-between gap-2">
                        <div className="text-sm">
                          {(() => {
                            const lib = addLibrarySessions.find((x) => x.id === addLibrarySessionId);
                            return lib ? (
                              <>
                                <div className="font-medium">#{lib.session_no} · {lib.session_name}</div>
                                <div className="text-xs text-muted-foreground capitalize">
                                  {(lib.session_kind || "").replace(/_/g, " ")} · from library
                                </div>
                              </>
                            ) : (
                              <span className="text-muted-foreground">Library entry not found.</span>
                            );
                          })()}
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => { setEditLibraryLocked(false); setAddLibrarySessionId(""); setTopic(CUSTOM_TOPIC); }}
                        >
                          Change
                        </Button>
                      </div>
                    ) : addLibrarySessions.length > 0 ? (
                      <div className="space-y-2">
                        <Input
                          value={addLibrarySearch}
                          onChange={(e) => setAddLibrarySearch(e.target.value)}
                          placeholder="Search library…"
                          className="h-8 text-xs"
                        />
                        <div className="max-h-40 overflow-y-auto border rounded divide-y">
                          {filteredAddLibrarySessions.map((s) => {
                            const isSelected = addLibrarySessionId === s.id && topic !== CUSTOM_TOPIC;
                            return (
                              <button
                                type="button"
                                key={s.id}
                                onClick={() => { setAddLibrarySessionId(s.id); setTopic(""); }}
                                className={`w-full text-left px-2 py-1.5 text-xs flex items-center justify-between gap-2 ${
                                  isSelected ? "bg-accent" : "hover:bg-muted"
                                }`}
                              >
                                <span className="truncate">#{s.session_no} · {s.session_name}</span>
                                {isSelected && <Badge className="text-[10px]">Selected</Badge>}
                              </button>
                            );
                          })}
                        </div>
                        <button
                          type="button"
                          onClick={() => { setTopic(CUSTOM_TOPIC); setAddLibrarySessionId(""); }}
                          className={`w-full text-left text-xs px-2 py-1.5 rounded border ${
                            topic === CUSTOM_TOPIC ? "bg-accent" : "hover:bg-muted"
                          }`}
                        >
                          + Custom session (not in library)
                        </button>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        No library entries — keep editing as a custom session below.
                      </p>
                    )}

                    {topic === CUSTOM_TOPIC && (
                      <div className="grid grid-cols-3 gap-2 pt-1">
                        <div className="space-y-1">
                          <Label className="text-xs">Session No</Label>
                          <Input
                            type="number"
                            min={1}
                            value={customSessionNo}
                            onChange={(e) => setCustomSessionNo(e.target.value)}
                            placeholder="e.g. 5"
                          />
                        </div>
                        <div className="space-y-1 col-span-2">
                          <Label className="text-xs">Session Name</Label>
                          <Input
                            value={customSessionName}
                            onChange={(e) => setCustomSessionName(e.target.value)}
                            placeholder="e.g. Cardiac arrhythmias"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={
                      (topic !== CUSTOM_TOPIC && !addLibrarySessionId) ||
                      (topic === CUSTOM_TOPIC && (!customSessionNo.trim() || !customSessionName.trim()))
                    }
                  >
                    Save Changes
                  </Button>
                </>
              )}
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {(() => {
        const idleSessions = sessions.filter(s => isIdleStatus(s.status));
        const openSessions = sessions.filter(s => s.status === "open");

        const renderCard = (s: any) => {
          const live = lastScanBySession[s.id];
          const lastScanAt = live?.lastScanAt ?? null;
          const isOpen = s.status === "open";
          const isIdle = isIdleStatus(s.status);
          const liveLabel = isIdle ? "Created" : (lastScanAt ? "Last scan" : "Opened");
          const liveTimestamp = isIdle ? s.created_at : (lastScanAt ?? s.opened_at ?? s.created_at);
          return (
            <Card key={s.id}>
              <CardContent className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 py-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {isOpen ? (
                      <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border-transparent gap-1.5">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                        </span>
                        Live · Open
                      </Badge>
                    ) : isIdle ? (
                      <Badge variant="secondary" className="gap-1.5">Not active</Badge>
                    ) : (
                      <Badge variant={sessionStatusVariant(s.status)}>{sessionStatusLabel(s.status)}</Badge>
                    )}
                    {s.excluded_from_coverage && (
                      <Badge variant="outline" className="text-muted-foreground">Not counted</Badge>
                    )}
                    <span className="text-xs text-muted-foreground">{s.module_code}</span>
                  </div>
                  <h3 className="font-semibold">{s.topic || "Untitled"}</h3>
                  <p className="text-sm text-muted-foreground">{s.class} • {new Date(s.session_date).toLocaleDateString()}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {liveLabel} • {formatRelativeShort(liveTimestamp, nowTick)}
                    {!isIdle && live?.scanCount ? ` • ${live.scanCount} scan${live.scanCount === 1 ? "" : "s"}` : ""}
                  </p>
                  {s.status === "closed" && summariesBySession[s.id] && (() => {
                    const sum = summariesBySession[s.id];
                    const ratePct = sum.attendance_rate != null
                      ? `${(Number(sum.attendance_rate) * 100).toFixed(1)}%`
                      : "—";
                    return (
                      <div className="mt-2 space-y-1">
                      <div className="flex flex-wrap gap-1.5">
                          <Badge variant="default">Present {sum.present_count}</Badge>
                          <Badge variant="secondary">Pending {sum.pending_count}</Badge>
                          {(sum.pending_approval_count > 0 || sum.pending_hod_confirm_count > 0) && (
                            <Badge variant="outline" className="text-xs">
                              ↳ AO {sum.pending_approval_count ?? 0} · HOD {sum.pending_hod_confirm_count ?? 0}
                            </Badge>
                          )}
                          <Badge variant="destructive">Rejected {sum.rejected_count}</Badge>
                          <Badge variant="outline">Absent {sum.absent_count}</Badge>
                          <Badge variant="outline">Rate {ratePct}</Badge>
                          {sum.cr_recorded_count > 0 && (
                            <Badge variant="outline">CR-recorded {sum.cr_recorded_count}</Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Generated {formatDistanceToNow(new Date(sum.generated_at), { addSuffix: true })}
                          {sum.total_enrolled > 0 ? ` • ${sum.total_enrolled} enrolled` : ""}
                        </p>
                      </div>
                    );
                  })()}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {isIdle && canManageSession && (
                    <Button
                      size="sm"
                      onClick={() => activateSession(s.id)}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      title="Activate this session to enable scanning"
                    >
                      <Power className="w-4 h-4 mr-1" />Activate
                    </Button>
                  )}
                  {isOpen && canManageSession && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" size="sm" title="Close this session and freeze the attendance summary">
                          <Square className="w-4 h-4 mr-1" />Close Session
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Close this session?</AlertDialogTitle>
                          <AlertDialogDescription>
                            "{s.topic || "Untitled"}" ({s.module_code}) will move from <strong>Open</strong> to <strong>Closed</strong>.
                            Scanning will stop, the final attendance summary will be generated, and your name will be recorded as the closer in the audit log.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => toggleSession(s.id, s.status)}>Close Session</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                  {s.status === "closed" && canManageSession && (
                    <ReopenSessionGuard
                      session={s}
                      onReopen={() => toggleSession(s.id, s.status)}
                      onStartNew={() => setStartOpen(true)}
                    />
                  )}
                  {s.status === "closed" && !s.remedial_parent_id && (
                    <RemedialSessionDialog session={s} />
                  )}
                  {s.status === "closed" && canManageSession && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" title="Refresh the frozen attendance summary for this closed session">
                          <RefreshCw className="w-4 h-4 mr-1" />Regenerate Summary
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Regenerate session summary?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will overwrite the frozen snapshot for "{s.topic || "Untitled"}" ({s.module_code}) with the latest attendance counts. Use this if records were added, approved, or removed after the session was closed.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => regenerateSummary(s.id)}>Regenerate</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                  {canManageSession && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => downloadTemplate(s)} title="Download attendance template">
                        <FileSpreadsheet className="w-4 h-4 mr-1" />Template
                      </Button>
                      {!isIdle && (
                        <Button variant="outline" size="sm" onClick={() => setBulkSession(s)} title="Bulk upload attendance">
                          <Upload className="w-4 h-4 mr-1" />Bulk Upload
                        </Button>
                      )}
                      {!isIdle && (() => {
                        const enabled = (approvedCountBySession[s.id] ?? 0) > 0;
                        return (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className={enabled ? undefined : "inline-block"}>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={!enabled}
                                    onClick={() => setAddCrSession(s)}
                                    title={enabled ? "Scan the CR's own attendance" : undefined}
                                  >
                                    <UserPlus className="w-4 h-4 mr-1" />Add CR Attendance
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              {!enabled && (
                                <TooltipContent>
                                  Available after at least one student attendance is approved for this session
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </TooltipProvider>
                        );
                      })()}
                      {!isIdle && <SessionPrintableSheet session={s} />}
                    </>
                  )}
                  {canManage && (
                    <>
                      <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => startEdit(s)} title="Edit session">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-9 w-9 text-destructive hover:text-destructive" title="Delete session">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete attendance session?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently delete the session "{s.topic || "Untitled"}" ({s.module_code}). Attendance records linked to this session may also be affected. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteSession(s.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        };

        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <div>
                <h3 className="text-base font-semibold">Sessions waiting to start</h3>
                <p className="text-xs text-muted-foreground">Created but not yet activated. Click <strong>Activate</strong> when class begins.</p>
              </div>
              {idleSessions.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">No sessions waiting.</p>
              ) : (
                <div className="grid gap-3">{idleSessions.map(renderCard)}</div>
              )}
            </div>

            <div className="space-y-2">
              <div>
                <h3 className="text-base font-semibold">Active sessions</h3>
                <p className="text-xs text-muted-foreground">Sessions currently open for scanning.</p>
              </div>
              {openSessions.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">No active sessions right now.</p>
              ) : (
                <div className="grid gap-3">{openSessions.map(renderCard)}</div>
              )}
            </div>
          </div>
        );
      })()}

      {bulkSession && (
        <SessionBulkAttendanceDialog
          open={!!bulkSession}
          onOpenChange={(o) => { if (!o) setBulkSession(null); }}
          session={bulkSession}
          onImported={load}
        />
      )}

      <SessionsBulkCreateDialog
        open={bulkCreateOpen}
        onOpenChange={setBulkCreateOpen}
        context={{
          semester: selectedSemester,
          class: selectedClass,
          moduleCode: selectedModule,
          moduleName: allModules.find((m) => m.module_code === selectedModule)?.module_name,
        }}
        onCreated={load}
      />

      {addCrSession && (
        <AddCrAttendanceDialog
          open={!!addCrSession}
          onOpenChange={(o) => { if (!o) { setAddCrSession(null); load(); } }}
          session={addCrSession}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Session Scanner Tab                                                */
/* ------------------------------------------------------------------ */
interface PendingStudent {
  id: string;
  name: string;
  program: string | null;
  nta_level: string | null;
  barcode: string;
  photoUrl: string | null;
  initials: string;
  user_id: string | null;
  signature_url: string | null;
}

function ScannerTab() {
  const [sessions, setSessions] = useState<any[]>([]);
  const [selectedSession, setSelectedSession] = useState("");
  const [scanning, setScanning] = useState(false);
  const [pendingStudent, setPendingStudent] = useState<PendingStudent | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [lastScan, setLastScan] = useState<{ name: string; status: "success" | "error"; message: string; kind?: "self_block" } | null>(null);
  const [endingSession, setEndingSession] = useState(false);
  const [sessionStatus, setSessionStatus] = useState<{ status: string; closed_at: string | null } | null>(null);
  const [requestingSelf, setRequestingSelf] = useState(false);
  const [cameraError, setCameraError] = useState<{
    kind: "permission" | "not_found" | "in_use" | "constraints" | "insecure" | "unsupported" | "unknown";
    title: string;
    guidance: string;
    raw: string;
  } | null>(null);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [retryingNow, setRetryingNow] = useState(false);
  const [retryCountdown, setRetryCountdown] = useState(0);
  const retryTimerRef = useRef<number | null>(null);
  const countdownTimerRef = useRef<number | null>(null);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const { toast } = useToast();
  const { user, hasRole, roles } = useAuth();
  const { checkVisibility } = useQrVisibility();
  const scanVisibility = checkVisibility("academic");

  // Load DB-driven bypass roles for the academic scanner (managed in Administration → Scanner Bypass).
  const { data: bypassRoles = [] } = useQuery({
    queryKey: ["qr-bypass-roles", "academic"],
    queryFn: async () => {
      const { data } = await supabase
        .from("qr_bypass_roles")
        .select("role")
        .eq("qr_type", "academic");
      return (data || []).map((r: any) => r.role as string);
    },
    staleTime: 5 * 60 * 1000,
  });

  const isCR = CR_ROLES.some((r) => hasRole(r as any));
  const isAcademicOrHod = hasRole("head_academic") || hasRole("head_administration") || hasRole("superadmin");
  // Superadmin always bypasses; everyone else is governed by the DB-managed bypass list.
  const canBypassQrGate = canBypassAcademicGate(roles as string[], bypassRoles);
  const effectiveScanVisible = canBypassQrGate ? true : scanVisibility.isVisible;

  // Class-Representative restriction: a CR (without any privileged override role)
  // may only scan sessions belonging to their own class (e.g. cr_pst4 → "PST4").
  // HODs (without head/admin overrides) are restricted to their department's classes
  // (hod_pst → PST4/PST5/PST6, etc.).
  const crOnly = isCrOnly(roles as string[]);
  const hodOnly = isHodOnly(roles as string[]);
  const scopedOnly = crOnly || hodOnly;
  const scopedAllowedClasses = useMemo(() => {
    const set = new Set<string>();
    if (crOnly) getCrAllowedClasses(roles as string[]).forEach((c) => set.add(c));
    if (hodOnly) getHodAllowedClasses(roles as string[]).forEach((c) => set.add(c));
    return Array.from(set);
  }, [roles, crOnly, hodOnly]);
  // Back-compat aliases for the rest of this function
  const crAllowedClasses = scopedAllowedClasses;
  const visibleSessions = useMemo(() => {
    if (!scopedOnly) return sessions;
    const allow = new Set(scopedAllowedClasses.map(normaliseClass));
    return sessions.filter((s) => allow.has(normaliseClass(s.class)));
  }, [sessions, scopedOnly, scopedAllowedClasses]);

  // Validation: scanning is only allowed when a correct, active academic session is selected.
  const selectedSessionRow = sessions.find((s) => s.id === selectedSession) || null;
  const todayEAT = (() => {
    const now = new Date();
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    return new Date(utc + 3 * 3600000).toISOString().split("T")[0];
  })();
  const isSessionActiveOpen = !!sessionStatus && sessionStatus.status === "open";
  const isSessionForToday = !!selectedSessionRow && (selectedSessionRow.session_date === todayEAT);
  const isSessionClassAllowedForCr = !scopedOnly
    || (!!selectedSessionRow && scopedAllowedClasses.map(normaliseClass).includes(normaliseClass(selectedSessionRow.class)));
  const sessionValidForScan = !!selectedSession && isSessionActiveOpen && isSessionForToday && isSessionClassAllowedForCr;
  const sessionInvalidReason = !selectedSession
    ? "Select an active academic session to begin scanning."
    : !isSessionActiveOpen
      ? "The selected session is not open. Activate or open the session before scanning."
      : !isSessionForToday
        ? `The selected session is scheduled for ${selectedSessionRow?.session_date ?? "another date"}, not today. Choose today's session.`
        : !isSessionClassAllowedForCr
          ? `Your role only allows scanning sessions for ${scopedAllowedClasses.join(", ") || "your assigned class"}. This session belongs to ${selectedSessionRow?.class ?? "another class"}.`
          : "";

  // Live clock for the status panel
  const [now, setNow] = useState<Date>(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    supabase.from("attendance_sessions").select("*").eq("status", "open").then(({ data }) => setSessions(data ?? []));
    return () => { scannerRef.current?.isScanning && scannerRef.current.stop().catch(() => {}); };
  }, []);

  // Track live status of the selected session (so the banner reflects an HOD closing it remotely)
  useEffect(() => {
    if (!selectedSession) { setSessionStatus(null); return; }
    let cancelled = false;
    const fetchStatus = async () => {
      const { data } = await supabase
        .from("attendance_sessions")
        .select("status, closed_at")
        .eq("id", selectedSession)
        .maybeSingle();
      if (!cancelled && data) setSessionStatus({ status: data.status, closed_at: data.closed_at });
    };
    fetchStatus();
    const channel = supabase
      .channel(`session-status-${selectedSession}`)
      .on("postgres_changes",
        { event: "UPDATE", schema: "public", table: "attendance_sessions", filter: `id=eq.${selectedSession}` },
        (payload: any) => {
          if (cancelled) return;
          const row = payload.new;
          setSessionStatus({ status: row.status, closed_at: row.closed_at });
        })
      .subscribe();
    const interval = setInterval(fetchStatus, 20000);
    return () => { cancelled = true; clearInterval(interval); supabase.removeChannel(channel); };
  }, [selectedSession]);

  // Auto-stop the camera and clear pending confirmation when the session becomes closed
  useEffect(() => {
    if (sessionStatus && sessionStatus.status !== "open") {
      if (scannerRef.current?.isScanning) {
        scannerRef.current.stop().catch(() => {});
      }
      setScanning(false);
      setPendingStudent(null);
      setLastScan({ name: "", status: "error", message: "Session is closed. No further scans can be recorded." });
    }
  }, [sessionStatus]);

  async function requestOwnAttendance() {
    if (!selectedSession || !user?.id) return;
    if (sessionStatus && sessionStatus.status !== "open") {
      toast({ title: "Session closed", description: "You can only request attendance while the session is open.", variant: "destructive" });
      return;
    }
    setRequestingSelf(true);
    try {
      const { data: me } = await supabase
        .from("students")
        .select("id, first_name, last_name")
        .eq("user_id", user.id)
        .maybeSingle();
      if (!me) {
        toast({ title: "Not a student", description: "Your account is not linked to a student record.", variant: "destructive" });
        return;
      }
      const { data: existing } = await supabase
        .from("attendance_records")
        .select("id, status")
        .eq("student_id", me.id)
        .eq("session_id", selectedSession)
        .eq("attendance_type", "session")
        .maybeSingle();
      if (existing) {
        toast({
          title: "Already on the list",
          description: existing.status === "present" ? "You are already marked present for this session." : "Your request is already pending review.",
        });
        return;
      }
      const { error } = await supabase.from("attendance_records").insert({
        student_id: me.id,
        session_id: selectedSession,
        attendance_type: "session",
        scanned_by: user.id,
        status: "pending_approval",
        notes: "Self-requested by Class Representative",
      });
      if (error) throw error;
      toast({ title: "Request sent", description: "The Academic Officer has been notified to confirm your attendance." });
      setLastScan({ name: `${me.first_name} ${me.last_name}`, status: "success", message: "Your attendance request was sent to the Academic Officer for approval." });
    } catch (e: any) {
      toast({ title: "Could not send request", description: e?.message ?? String(e), variant: "destructive" });
    } finally {
      setRequestingSelf(false);
    }
  }

  async function handleScan(decodedText: string) {
    if (!selectedSession) return;
    // Hard block: if session is closed (per live status), refuse to process the scan
    if (sessionStatus && sessionStatus.status !== "open") {
      await stopScanner();
      setLastScan({ name: decodedText, status: "error", message: "Session is closed. No further scans can be recorded." });
      return;
    }
    // Hard block: CR may only scan their own class's session
    if (crOnly && selectedSessionRow && !crAllowedClasses.map(normaliseClass).includes(normaliseClass(selectedSessionRow.class))) {
      await stopScanner();
      setLastScan({
        name: decodedText,
        status: "error",
        message: `You can only scan sessions for ${crAllowedClasses.join(", ") || "your class"}. This session belongs to ${selectedSessionRow.class ?? "another class"}.`,
      });
      return;
    }
    // Resolve rotating QR token (or pass through legacy ACA- code)
    const resolved = await resolveScannedQr(decodedText, "academic", "session_scanner");
    if (!resolved.ok) {
      await stopScanner();
      setLastScan({ name: decodedText, status: "error", message: resolved.message ?? `QR code rejected (${resolved.reason ?? "invalid"})` });
      return;
    }
    const lookupCode = resolved.resolvedCode ?? decodedText;
    if (!lookupCode.startsWith("ACA-")) {
      await stopScanner();
      setLastScan({ name: decodedText, status: "error", message: "Only Academic (ACA) barcodes are accepted for session attendance" });
      return;
    }

    const { data: student } = await supabase
      .from("students")
      .select("id, first_name, last_name, student_number, program, nta_level, user_id, signature_url")
      .eq("barcode_academic", lookupCode)
      .maybeSingle();

    if (!student) {
      await stopScanner();
      setLastScan({ name: decodedText, status: "error", message: "Student not found" });
      return;
    }

    // Block CR self-scan
    if (isCR && student.user_id && student.user_id === user?.id) {
      await stopScanner();
      toast({ title: "Self-scan blocked", variant: "destructive" });
      setLastScan({
        name: `${student.first_name} ${student.last_name}`,
        status: "error",
        kind: "self_block",
        message: "As a Class Representative you cannot scan your own barcode. Ask the Academic Officer or HOD to record your attendance using the Add CR Attendance tool.",
      });
      return;
    }

    // Remedial-session guard: only allow students who were Absent in the parent session.
    const remedialParentId = (selectedSessionRow as any)?.remedial_parent_id as string | null | undefined;
    if (remedialParentId) {
      const { data: parentRec } = await supabase
        .from("attendance_records")
        .select("status")
        .eq("session_id", remedialParentId)
        .eq("student_id", student.id)
        .eq("attendance_type", "session")
        .maybeSingle();
      if (parentRec && parentRec.status === "present") {
        await stopScanner();
        setLastScan({
          name: `${student.first_name} ${student.last_name}`,
          status: "error",
          message: "This student was already Present in the original session. Remedial scans are reserved for absentees.",
        });
        return;
      }
    }

    const fullName = `${student.first_name} ${student.last_name}`;

    const { data: existing } = await supabase
      .from("attendance_records")
      .select("id")
      .eq("student_id", student.id)
      .eq("session_id", selectedSession)
      .eq("attendance_type", "session")
      .maybeSingle();

    if (existing) {
      await stopScanner();
      setLastScan({ name: fullName, status: "error", message: "Already scanned for this session" });
      return;
    }

    // Fetch photo
    let photoUrl: string | null = null;
    const { data: bio } = await supabase
      .from("student_biometrics")
      .select("photo_path")
      .eq("student_id", student.id)
      .maybeSingle();
    if (bio?.photo_path) {
      photoUrl = await getSignedFileUrl(bio.photo_path);
    }

    await stopScanner();
    setPendingStudent({
      id: student.id,
      name: fullName,
      program: student.program,
      nta_level: student.nta_level,
      barcode: decodedText,
      photoUrl,
      initials: `${student.first_name.charAt(0)}${student.last_name.charAt(0)}`.toUpperCase(),
      user_id: student.user_id,
      signature_url: student.signature_url,
    });
  }

  async function handleConfirm() {
    if (!pendingStudent) return;
    setConfirming(true);

    // Re-verify session is still open (it may have been closed by HOD while scanner was active)
    const { data: sess } = await supabase
      .from("attendance_sessions")
      .select("status")
      .eq("id", selectedSession)
      .maybeSingle();
    if (!sess || sess.status !== "open") {
      setPendingStudent(null);
      setConfirming(false);
      setSessions((prev) => prev.filter((s) => s.id !== selectedSession));
      setSelectedSession("");
      setLastScan({ name: pendingStudent.name, status: "error", message: "Session is closed. No further scans can be recorded." });
      return;
    }

    const status = isAcademicOrHod ? "present" : "pending_approval";
    const { error } = await supabase.from("attendance_records").insert({
      student_id: pendingStudent.id,
      session_id: selectedSession,
      attendance_type: "session",
      scanned_by: user?.id,
      status,
      ...(status === "present" ? { approved_by: user?.id, approved_at: new Date().toISOString() } : {}),
    });

    const name = pendingStudent.name;
    setPendingStudent(null);
    setConfirming(false);
    const isDup = error && (
      (error as any).code === "23505" ||
      /attendance_one_session_scan_per_student|duplicate key/i.test(error.message ?? "")
    );
    setLastScan(error
      ? isDup
        ? { name, status: "error", message: "Already marked present for this session" }
        : { name, status: "error", message: error.message }
      : { name, status: "success", message: status === "present" ? "Attendance recorded!" : "Saved — pending Academic Officer approval" });
  }

  function classifyCameraError(err: any) {
    const name: string = err?.name ?? "";
    const message: string = err?.message ?? String(err ?? "Unknown error");
    const blob = `${name} ${message}`.toLowerCase();

    if (typeof window !== "undefined" && !window.isSecureContext) {
      return {
        kind: "insecure" as const,
        title: "Camera blocked: insecure connection",
        guidance: "Cameras only work over HTTPS. Open this page using https:// (or localhost) and try again.",
        raw: message,
      };
    }
    if (typeof navigator !== "undefined" && !navigator.mediaDevices?.getUserMedia) {
      return {
        kind: "unsupported" as const,
        title: "Camera not supported by this browser",
        guidance: "Use a modern browser like Chrome, Edge, Safari, or Firefox. In-app browsers (Facebook, TikTok) often block cameras — open the link in your real browser.",
        raw: message,
      };
    }
    if (name === "NotAllowedError" || name === "PermissionDeniedError" || blob.includes("permission")) {
      return {
        kind: "permission" as const,
        title: "Camera permission denied",
        guidance: "Tap the lock/camera icon in your browser's address bar, allow camera access for this site, then press Retry camera.",
        raw: message,
      };
    }
    if (name === "NotFoundError" || name === "DevicesNotFoundError" || blob.includes("not found") || blob.includes("no camera")) {
      return {
        kind: "not_found" as const,
        title: "No camera found",
        guidance: "Connect a webcam (or unplug and re-plug it), make sure it's not disabled in system settings, then press Retry camera.",
        raw: message,
      };
    }
    if (name === "NotReadableError" || name === "TrackStartError" || blob.includes("in use") || blob.includes("could not start") || blob.includes("readable")) {
      return {
        kind: "in_use" as const,
        title: "Camera is already in use",
        guidance: "Close other apps or browser tabs using the camera (Zoom, Teams, Meet, another scanner tab), then press Retry camera.",
        raw: message,
      };
    }
    if (name === "OverconstrainedError" || name === "ConstraintNotSatisfiedError" || blob.includes("constraint")) {
      return {
        kind: "constraints" as const,
        title: "Rear camera unavailable",
        guidance: "Your device may not have a rear-facing camera. We'll automatically try the front camera on the next retry.",
        raw: message,
      };
    }
    return {
      kind: "unknown" as const,
      title: "Camera failed to open",
      guidance: "Refresh the page if this keeps happening, or try a different browser. Press Retry camera to try once more.",
      raw: message,
    };
  }

  function clearRetryTimers() {
    if (retryTimerRef.current) { window.clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
    if (countdownTimerRef.current) { window.clearInterval(countdownTimerRef.current); countdownTimerRef.current = null; }
    setRetryCountdown(0);
  }

  function scheduleAutoRetry(nextAttempt: number) {
    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, capped at 30s. Stop after 5 auto-retries.
    if (nextAttempt > 5) return;
    const delaySec = Math.min(30, Math.pow(2, nextAttempt - 1));
    clearRetryTimers();
    setRetryCountdown(delaySec);
    countdownTimerRef.current = window.setInterval(() => {
      setRetryCountdown((s) => (s > 1 ? s - 1 : 0));
    }, 1000);
    retryTimerRef.current = window.setTimeout(() => {
      clearRetryTimers();
      void startScanner({ isRetry: true });
    }, delaySec * 1000);
  }

  useEffect(() => () => clearRetryTimers(), []);

  async function startScanner(opts: { isRetry?: boolean; manual?: boolean } = {}) {
    if (!selectedSession) { toast({ title: "Select a session first", variant: "destructive" }); return; }
    if (!effectiveScanVisible) { toast({ title: "Scanning not allowed", description: "Your role is not permitted to scan at this time.", variant: "destructive" }); return; }
    if (!isSessionActiveOpen) {
      toast({ title: "Session is closed", description: "Activate or open the session before scanning.", variant: "destructive" });
      return;
    }
    if (!isSessionForToday) {
      toast({ title: "Wrong session date", description: `This session is scheduled for ${selectedSessionRow?.session_date ?? "another date"}. Pick today's session.`, variant: "destructive" });
      return;
    }
    if (scannerRef.current?.isScanning) return;

    if (opts.manual) clearRetryTimers();
    if (opts.isRetry || opts.manual) setRetryingNow(true);

    const sessionIdAtStart = selectedSession;
    const scanner = new Html5Qrcode("attendance-qr-reader");
    scannerRef.current = scanner;
    // If previous failure was constraints-related (no rear cam), fall back to "user" facing.
    const facingMode = cameraError?.kind === "constraints" ? "user" : "environment";
    try {
      await scanner.start({ facingMode }, { fps: 10, qrbox: { width: 250, height: 250 } }, handleScan, () => {});
      setScanning(true);
      // Success → reset error & retry state
      setCameraError(null);
      setRetryAttempt(0);
      setRetryingNow(false);
      clearRetryTimers();
      // Re-check after camera opens so browser media permission stays tied to the click gesture.
      supabase
        .from("attendance_sessions")
        .select("status, closed_at, session_date")
        .eq("id", sessionIdAtStart)
        .maybeSingle()
        .then(async ({ data: sess }) => {
          if (selectedSession !== sessionIdAtStart) return;
          if (!sess || sess.status !== "open" || sess.session_date !== todayEAT) {
            setSessionStatus(sess ? { status: sess.status, closed_at: sess.closed_at } : null);
            if (scanner.isScanning) await scanner.stop().catch(() => {});
            setScanning(false);
            toast({ title: "Session is not active", description: "The scanner was stopped because the selected session is no longer valid.", variant: "destructive" });
          }
        });
      // Audit: record when a user actually starts the scanner via role-based bypass
      if (canBypassQrGate) {
        supabase
          .rpc("log_qr_bypass_scan_start", { _session_id: selectedSession, _qr_type: "academic" })
          .then(({ error }) => { if (error) console.warn("[audit] qr bypass log failed:", error.message); });
      }
    } catch (err: any) {
      const classified = classifyCameraError(err);
      setCameraError(classified);
      setRetryingNow(false);
      const nextAttempt = retryAttempt + 1;
      setRetryAttempt(nextAttempt);
      // Don't auto-retry permission/insecure/unsupported — those need user action.
      const userActionable = classified.kind === "permission" || classified.kind === "insecure" || classified.kind === "unsupported";
      if (!userActionable) scheduleAutoRetry(nextAttempt);
      toast({ title: classified.title, description: classified.guidance, variant: "destructive" });
    }
  }

  async function retryCameraNow() {
    setCameraError(null);
    await startScanner({ manual: true });
  }

  async function stopScanner() {
    clearRetryTimers();
    setCameraError(null);
    setRetryAttempt(0);
    if (scannerRef.current?.isScanning) await scannerRef.current.stop();
    setScanning(false);
  }

  async function endSession() {
    if (!selectedSession) return;
    setEndingSession(true);
    const closedAt = new Date();
    const sessionId = selectedSession;
    const { error } = await supabase
      .from("attendance_sessions")
      .update({ status: "closed", closed_at: closedAt.toISOString() })
      .eq("id", sessionId);
    setEndingSession(false);
    if (error) {
      toast({ title: "Could not end session", description: error.message, variant: "destructive" });
      return;
    }
    const { error: sumErr } = await supabase.rpc("generate_session_summary", { _session_id: sessionId });
    await stopScanner();
    setSessions((prev) => prev.filter((s) => s.id !== sessionId));
    setSelectedSession("");
    setPendingStudent(null);
    setLastScan(null);
    if (sumErr) {
      toast({ title: `Session ended at ${format(closedAt, "HH:mm")}`, description: `Summary not generated: ${sumErr.message}`, variant: "destructive" });
    } else {
      toast({ title: `Session ended at ${format(closedAt, "HH:mm")}`, description: "Final attendance summary generated." });
    }
  }

  return (
    <div className="max-w-lg mx-auto space-y-6">
      <div className="space-y-2">
        <Label>Active Session</Label>
        <Select value={selectedSession} onValueChange={setSelectedSession}>
          <SelectTrigger><SelectValue placeholder="Select an open session" /></SelectTrigger>
          <SelectContent>
            {visibleSessions.length === 0 && (
              <div className="px-3 py-2 text-xs text-muted-foreground">
                {crOnly
                  ? `No open sessions for your class (${crAllowedClasses.join(", ") || "—"}).`
                  : "No open sessions."}
              </div>
            )}
            {visibleSessions.map(s => (
              <SelectItem key={s.id} value={s.id}>{s.module_code} — {s.topic}{s.class ? ` · ${s.class}` : ""}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedSession && sessionStatus && (
        sessionStatus.status === "open" ? (
          <div className="flex items-center gap-2 rounded-md border border-success/40 bg-success/10 px-3 py-2 text-sm">
            <CheckCircle2 className="w-4 h-4 text-success shrink-0" />
            <div className="flex-1">
              <span className="font-medium text-success">Session Open</span>
              <span className="text-muted-foreground"> · scanning is allowed</span>
            </div>
            <Badge variant="default" className="bg-success text-success-foreground">OPEN</Badge>
          </div>
        ) : (
          <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
            <XCircle className="w-4 h-4 text-destructive shrink-0" />
            <div className="flex-1">
              <span className="font-medium text-destructive">Session Closed</span>
              {sessionStatus.closed_at && (
                <span className="text-muted-foreground">
                  {" "}· closed at {format(new Date(sessionStatus.closed_at), "PPp")} ({formatDistanceToNow(new Date(sessionStatus.closed_at), { addSuffix: true })})
                </span>
              )}
              <div className="text-xs text-muted-foreground mt-0.5">No further scans can be recorded.</div>
            </div>
            <Badge variant="destructive">CLOSED</Badge>
          </div>
        )
      )}

      {/* Live Scanner Status Panel — confirms session, course/class, time and role permissions in real time */}
      <Card className={cn(
        "border-2 transition-colors",
        scanning ? "border-success/60 bg-success/5" :
        sessionValidForScan && effectiveScanVisible ? "border-primary/40 bg-primary/5" :
        "border-muted bg-muted/30"
      )}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Radio className={cn("w-4 h-4", scanning ? "text-success animate-pulse" : "text-muted-foreground")} />
              Scanner Status
            </CardTitle>
            <Badge variant={scanning ? "default" : "secondary"} className={scanning ? "bg-success text-success-foreground" : ""}>
              {scanning ? "LIVE" : sessionValidForScan && effectiveScanVisible ? "READY" : "STANDBY"}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-2 text-xs sm:text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="flex items-start gap-2">
              <CalendarCheck className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <div className="text-muted-foreground text-[11px] uppercase tracking-wide">Session</div>
                <div className="font-medium truncate">
                  {selectedSessionRow?.topic || (selectedSession ? "Untitled session" : "No session selected")}
                </div>
                {selectedSessionRow && (
                  <div className="text-muted-foreground text-[11px]">
                    {selectedSessionRow.session_date}
                    {sessionStatus && (
                      <span className={cn("ml-1 font-medium", sessionStatus.status === "open" ? "text-success" : "text-destructive")}>
                        · {sessionStatus.status.toUpperCase()}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-start gap-2">
              <BookOpen className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <div className="text-muted-foreground text-[11px] uppercase tracking-wide">Module</div>
                <div className="font-medium truncate">{selectedSessionRow?.module_code || "—"}</div>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <GraduationCap className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <div className="text-muted-foreground text-[11px] uppercase tracking-wide">Class</div>
                <div className="font-medium truncate">{(selectedSessionRow as any)?.class || "—"}</div>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <ClipboardList className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <div className="text-muted-foreground text-[11px] uppercase tracking-wide">Now (EAT)</div>
                <div className="font-medium tabular-nums">
                  {format(now, "EEE, dd MMM · HH:mm:ss")}
                </div>
              </div>
            </div>
          </div>

          <div className="border-t pt-2 flex items-start gap-2">
            {effectiveScanVisible ? (
              <ShieldCheck className="w-4 h-4 mt-0.5 text-success shrink-0" />
            ) : (
              <ShieldAlert className="w-4 h-4 mt-0.5 text-destructive shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-muted-foreground text-[11px] uppercase tracking-wide">Permission</div>
              <div className={cn("font-medium", effectiveScanVisible ? "text-success" : "text-destructive")}>
                {effectiveScanVisible
                  ? canBypassQrGate ? "Allowed (role bypass)" : "Allowed (visibility rule active)"
                  : "Not allowed"}
              </div>
              <div className="text-muted-foreground text-[11px] truncate">
                Role: {roles.length ? roles.map((r) => r.replace(/_/g, " ")).join(", ") : "none"}
              </div>
              {!effectiveScanVisible && scanVisibility.reason && (
                <div className="text-destructive text-[11px] mt-0.5">{scanVisibility.reason}</div>
              )}
              {effectiveScanVisible && !sessionValidForScan && sessionInvalidReason && (
                <div className="text-warning-foreground text-[11px] mt-0.5">{sessionInvalidReason}</div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        <div id="attendance-qr-reader" className="w-full" style={{ minHeight: scanning ? 300 : 0 }} />
        {!scanning && !pendingStudent && !lastScan && (
          <CardContent className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <ScanLine className="w-16 h-16 mb-4 opacity-30" />
            <p>Camera is off. Start scanning to capture attendance.</p>
          </CardContent>
        )}
      </Card>

      {!pendingStudent && !lastScan && (
        <div className="space-y-3">
          {!effectiveScanVisible && (
            <Card className="border-destructive/30 bg-destructive/5">
              <CardContent className="flex items-center gap-3 py-3">
                <ShieldAlert className="w-5 h-5 text-destructive shrink-0" />
                <p className="text-sm text-destructive">{scanVisibility.reason}</p>
              </CardContent>
            </Card>
          )}
          {effectiveScanVisible && !sessionValidForScan && (
            <Card className="border-warning/40 bg-warning/10">
              <CardContent className="flex items-center gap-3 py-3">
                <ShieldAlert className="w-5 h-5 text-warning shrink-0" />
                <p className="text-sm text-warning-foreground">{sessionInvalidReason}</p>
              </CardContent>
            </Card>
          )}
          {cameraError && !scanning && (
            <Card className="border-destructive/40 bg-destructive/5">
              <CardContent className="py-3 space-y-3">
                <div className="flex items-start gap-3">
                  <CameraOff className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-destructive">{cameraError.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">{cameraError.guidance}</p>
                    {retryAttempt > 0 && (
                      <p className="text-[11px] text-muted-foreground mt-1">
                        Attempt {retryAttempt}
                        {retryCountdown > 0 && ` · auto-retrying in ${retryCountdown}s`}
                        {retryCountdown === 0 && retryAttempt >= 5 && " · automatic retries stopped"}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => void retryCameraNow()} size="sm" disabled={retryingNow}>
                    <RefreshCw className={cn("w-4 h-4 mr-2", retryingNow && "animate-spin")} />
                    {retryingNow ? "Retrying…" : retryCountdown > 0 ? "Retry now" : "Retry camera"}
                  </Button>
                  {retryCountdown > 0 && (
                    <Button onClick={clearRetryTimers} size="sm" variant="ghost">Cancel auto-retry</Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          <div className="flex gap-3">
            {!scanning ? (
              <Button onClick={() => void startScanner()} className="flex-1" size="lg" disabled={!effectiveScanVisible || !sessionValidForScan || retryingNow}><Camera className="w-5 h-5 mr-2" />Start Scanning</Button>
            ) : (
              <Button onClick={stopScanner} variant="destructive" className="flex-1" size="lg"><CameraOff className="w-5 h-5 mr-2" />Stop Scanner</Button>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="lg" disabled={!selectedSession || endingSession}>End Session</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>End this session now?</AlertDialogTitle>
                  <AlertDialogDescription>No further scans will be accepted for this session.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={endSession}>End Session</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
          {isCR && selectedSession && (
            <Button
              onClick={requestOwnAttendance}
              variant="secondary"
              className="w-full"
              disabled={requestingSelf || (!!sessionStatus && sessionStatus.status !== "open")}
            >
              {requestingSelf ? "Sending request…" : "Request my attendance (CR)"}
            </Button>
          )}
        </div>
      )}

      {pendingStudent && (
        <Card className="border-primary/50 bg-primary/5">
          <CardContent className="py-6 space-y-4">
            <div className="flex flex-col items-center text-center gap-3">
              <Avatar className="h-24 w-24">
                {pendingStudent.photoUrl && <AvatarImage src={pendingStudent.photoUrl} alt={pendingStudent.name} />}
                <AvatarFallback className="bg-primary/10 text-primary font-bold text-2xl">{pendingStudent.initials}</AvatarFallback>
              </Avatar>
              <div>
                <p className="font-semibold text-xl">{pendingStudent.name}</p>
                <p className="text-sm text-muted-foreground">{pendingStudent.program || "No program"}{pendingStudent.nta_level ? ` — NTA ${pendingStudent.nta_level}` : ""}</p>
                <p className="text-xs font-mono text-muted-foreground mt-1">{pendingStudent.barcode}</p>
              </div>
            </div>
            <Button className="w-full" size="lg" onClick={handleConfirm} disabled={confirming}>
              <CheckCircle2 className="w-5 h-5 mr-2" />{confirming ? "Confirming..." : "Confirm Attendance"}
            </Button>
          </CardContent>
        </Card>
      )}

      {lastScan && (
        <Card className={
          lastScan.kind === "self_block"
            ? "border-warning/60 bg-warning/10"
            : lastScan.status === "success"
              ? "border-primary/50 bg-primary/5"
              : "border-destructive/50 bg-destructive/5"
        }>
          <CardContent className="py-6 space-y-4">
            <div className="flex items-start gap-4">
              {lastScan.kind === "self_block"
                ? <AlertTriangle className="w-10 h-10 text-warning shrink-0" />
                : lastScan.status === "success"
                  ? <CheckCircle2 className="w-10 h-10 text-primary shrink-0" />
                  : <XCircle className="w-10 h-10 text-destructive shrink-0" />}
              <div className="flex-1">
                <p className="font-semibold text-lg">
                  {lastScan.kind === "self_block" ? "Self-scan not allowed" : lastScan.name}
                </p>
                {lastScan.kind === "self_block" && (
                  <p className="text-xs text-muted-foreground mt-0.5">{lastScan.name}</p>
                )}
                <p className="text-sm text-muted-foreground mt-1">{lastScan.message}</p>
              </div>
              {lastScan.status === "success" && (
                <Badge className="ml-auto text-base px-4 py-1">PRESENT</Badge>
              )}
            </div>
            <div className="flex gap-3">
              <Button
                className="flex-1"
                size="lg"
                onClick={() => { setLastScan(null); startScanner(); }}
              >
                <Camera className="w-5 h-5 mr-2" />Scan Next
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="flex-1" size="lg" disabled={!selectedSession || endingSession}>End Session</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>End this session now?</AlertDialogTitle>
                    <AlertDialogDescription>No further scans will be accepted for this session.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={endSession}>End Session</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
            {isCR && lastScan.kind === "self_block" && selectedSession && (
              <Button
                onClick={requestOwnAttendance}
                variant="secondary"
                className="w-full"
                disabled={requestingSelf || (!!sessionStatus && sessionStatus.status !== "open")}
              >
                {requestingSelf ? "Sending request…" : "Request my attendance from Academic Officer"}
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Gate Attendance Tab                                                */
/* ------------------------------------------------------------------ */
interface CustomTask {
  id: string;
  attendance_date: string;
  notes: string | null;
  status: string;
}

function GateTab({ activeTask, onTaskComplete }: { activeTask?: CustomTask | null; onTaskComplete?: () => void }) {
  const [mode, setMode] = useState<"entry" | "exit">("entry");
  const [scanning, setScanning] = useState(false);
  const [pendingStudent, setPendingStudent] = useState<PendingStudent & { admNumber: string } | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [lastScan, setLastScan] = useState<{
    name: string;
    status: "success" | "error";
    message: string;
    admNumber?: string;
  } | null>(null);
  const [todayLog, setTodayLog] = useState<any[]>([]);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const processingRef = useRef(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const { checkVisibility } = useQrVisibility();
  const scanVisibility = checkVisibility("admin");

  // Use task date if active, otherwise today
  const effectiveDate = activeTask ? activeTask.attendance_date : new Date().toISOString().split("T")[0];

  const loadTodayLog = useCallback(async () => {
    const { data } = await supabase
      .from("attendance_records")
      .select("*, students(first_name, last_name, barcode_admin, signature_url)")
      .eq("attendance_date", effectiveDate)
      .in("attendance_type", ["general_entry", "general_exit"])
      .order("scanned_at", { ascending: true });

    const grouped: Record<string, any> = {};
    (data ?? []).forEach((r: any) => {
      const key = r.student_id;
      if (!grouped[key]) {
        grouped[key] = {
          student_id: key,
          student_name: r.students ? `${r.students.first_name} ${r.students.last_name}` : "Unknown",
          adm_number: r.registration_number || r.students?.barcode_admin || "—",
          time_in: null,
          time_out: null,
          signature_url: r.students?.signature_url || null,
          entry_record_id: null,
        };
      }
      if (r.attendance_type === "general_entry") {
        grouped[key].time_in = r.scanned_at;
        grouped[key].entry_record_id = r.id;
      }
      if (r.attendance_type === "general_exit") {
        grouped[key].time_out = r.scanned_at;
      }
    });
    setTodayLog(Object.values(grouped));
  }, [effectiveDate]);

  useEffect(() => { loadTodayLog(); }, [loadTodayLog]);

  useEffect(() => {
    return () => { scannerRef.current?.isScanning && scannerRef.current.stop().catch(() => {}); };
  }, []);

  async function handleScan(decodedText: string) {
    // Lock against rapid re-fires from the 10fps scanner loop
    if (processingRef.current) return;
    processingRef.current = true;
    // Stop the camera immediately so it can't fire again before awaits resolve
    await stopScanner();

    try {
      const resolved = await resolveScannedQr(decodedText, "admin", "gate_scanner");
      if (!resolved.ok) {
        const friendly =
          resolved.reason === "expired"
            ? "QR imeisha muda. Mwanafunzi abonyeze Sync au awashe Auto Date & Time kwenye simu."
            : resolved.reason === "future_token"
            ? "Saa ya simu ya mwanafunzi iko mbele. Awashe Auto Date & Time."
            : resolved.reason === "replayed"
            ? "QR hii imeshatumika. Mwanafunzi aionyeshe upya kutoka dashboard."
            : resolved.reason === "invalid_signature"
            ? "QR si halali (huenda ni screenshot ya zamani). Afungue dashboard yake."
            : resolved.message ?? `QR code rejected (${resolved.reason ?? "invalid"})`;
        setLastScan({ name: decodedText, status: "error", message: friendly });
        return;
      }

      // Build a student lookup that works for both rotating tokens (any QR type)
      // and legacy printed cards (ADM/ACA/EXM/ACC).
      const studentSelect =
        "id, first_name, last_name, barcode_admin, signature_url, user_id, program, nta_level";
      let student: any = null;
      if (resolved.studentId) {
        const { data } = await supabase
          .from("students")
          .select(studentSelect)
          .eq("id", resolved.studentId)
          .maybeSingle();
        student = data;
      } else {
        const lookupCode = resolved.resolvedCode ?? decodedText;
        const col = lookupCode.startsWith("ACA-")
          ? "barcode_academic"
          : lookupCode.startsWith("EXM-")
          ? "barcode_examination"
          : lookupCode.startsWith("ACC-")
          ? "barcode_accounting"
          : "barcode_admin";
        const { data } = await supabase
          .from("students")
          .select(studentSelect)
          .eq(col, lookupCode)
          .maybeSingle();
        student = data;
      }

      if (!student) {
        setLastScan({ name: decodedText, status: "error", message: "Student not found" });
        return;
      }

      const fullName = `${student.first_name} ${student.last_name}`;

      // Duplicate checks before showing preview
      if (mode === "entry") {
        const { data: existing } = await supabase
          .from("attendance_records")
          .select("id")
          .eq("student_id", student.id)
          .eq("attendance_type", "general_entry")
          .eq("attendance_date", effectiveDate)
          .maybeSingle();
        if (existing) {
          setLastScan({ name: fullName, status: "error", message: "Already scanned for entry today", admNumber: decodedText });
          return;
        }
      } else {
        const { data: entryRecord } = await supabase
          .from("attendance_records")
          .select("id")
          .eq("student_id", student.id)
          .eq("attendance_type", "general_entry")
          .eq("attendance_date", effectiveDate)
          .maybeSingle();
        if (!entryRecord) {
          setLastScan({ name: fullName, status: "error", message: "No entry record found for today", admNumber: decodedText });
          return;
        }
        const { data: existingExit } = await supabase
          .from("attendance_records")
          .select("id")
          .eq("student_id", student.id)
          .eq("attendance_type", "general_exit")
          .eq("attendance_date", effectiveDate)
          .maybeSingle();
        if (existingExit) {
          setLastScan({ name: fullName, status: "error", message: "Already scanned for exit today", admNumber: decodedText });
          return;
        }
      }

      // Fetch photo
      let photoUrl: string | null = null;
      const { data: bio } = await supabase
        .from("student_biometrics")
        .select("photo_path")
        .eq("student_id", student.id)
        .maybeSingle();
      if (bio?.photo_path) {
        photoUrl = await getSignedFileUrl(bio.photo_path);
      }

      setPendingStudent({
        id: student.id,
        name: fullName,
        program: student.program,
        nta_level: student.nta_level,
        barcode: decodedText,
        admNumber: decodedText,
        photoUrl,
        initials: `${student.first_name.charAt(0)}${student.last_name.charAt(0)}`.toUpperCase(),
        user_id: student.user_id,
        signature_url: student.signature_url,
      });
    } finally {
      processingRef.current = false;
    }
  }

  async function handleConfirm() {
    if (!pendingStudent) return;
    setConfirming(true);
    const attendanceType = mode === "entry" ? "general_entry" : "general_exit";

    const { error } = await supabase.from("attendance_records").insert({
      student_id: pendingStudent.id,
      attendance_type: attendanceType,
      attendance_date: effectiveDate,
      scanned_by: user?.id,
      registration_number: pendingStudent.admNumber,
      signature_url: pendingStudent.signature_url || null,
    });

    const name = pendingStudent.name;
    const admNumber = pendingStudent.admNumber;
    const userId = pendingStudent.user_id;

    setPendingStudent(null);
    setConfirming(false);

    if (error) {
      const friendly = (error as any).code === "23505"
        ? `Already scanned for ${mode} today`
        : error.message;
      setLastScan({ name, status: "error", message: friendly, admNumber });
      if ((error as any).code === "23505") loadTodayLog();
    } else {
      const label = mode === "entry" ? "Entry recorded!" : "Exit recorded!";
      setLastScan({ name, status: "success", message: label, admNumber });
      if (userId) {
        const title = mode === "entry" ? "Present ✅" : "Exit Recorded 🚪";
        const msg = mode === "entry"
          ? `Your attendance was recorded at ${new Date().toLocaleTimeString("en-GB", { timeZone: "Africa/Dar_es_Salaam" })}`
          : `Your exit was recorded at ${new Date().toLocaleTimeString("en-GB", { timeZone: "Africa/Dar_es_Salaam" })}`;
        await supabase.from("notifications").insert({ user_id: userId, title, message: msg, type: "info" });
      }
      loadTodayLog();
    }
  }

  async function startScanner() {
    const scanner = new Html5Qrcode("gate-qr-reader");
    scannerRef.current = scanner;
    try {
      await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 250, height: 250 } }, handleScan, () => {});
      setScanning(true);
    } catch (err: any) {
      toast({ title: "Camera error", description: err.toString(), variant: "destructive" });
    }
  }

  async function stopScanner() {
    if (scannerRef.current?.isScanning) await scannerRef.current.stop();
    setScanning(false);
  }

  const formatTime = (ts: string | null) => {
    if (!ts) return "—";
    return new Date(ts).toLocaleTimeString("en-GB", { timeZone: "Africa/Dar_es_Salaam", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  };

  return (
    <div className="space-y-6">
      {activeTask && (
        <Card className="border-amber-500/50 bg-amber-500/10">
          <CardContent className="py-4 space-y-2">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
              <p className="font-semibold text-amber-700">Backdated Attendance — {format(new Date(activeTask.attendance_date + "T00:00:00"), "PPP")}</p>
            </div>
            {activeTask.notes && <p className="text-sm text-muted-foreground ml-7">{activeTask.notes}</p>}
            <div className="ml-7">
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  await supabase.from("custom_attendance_tasks").update({ status: "completed" }).eq("id", activeTask.id);
                  onTaskComplete?.();
                }}
              >
                <CheckCircle2 className="w-4 h-4 mr-1" /> Mark Task Complete
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      <div className="max-w-lg mx-auto space-y-6">
        <Tabs value={mode} onValueChange={v => setMode(v as "entry" | "exit")}>
          <TabsList className="w-full">
            <TabsTrigger value="entry" className="flex-1">🌅 Morning Entry</TabsTrigger>
            <TabsTrigger value="exit" className="flex-1">🌆 Evening Exit</TabsTrigger>
          </TabsList>
        </Tabs>

        <Card className="overflow-hidden">
          <div id="gate-qr-reader" className="w-full" style={{ minHeight: scanning ? 300 : 0 }} />
          {!scanning && !pendingStudent && !lastScan && (
            <CardContent className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <DoorOpen className="w-16 h-16 mb-4 opacity-30" />
              <p>Ready for {mode === "entry" ? "morning entry" : "evening exit"} scanning</p>
              <p className="text-xs mt-1">Only Administration (ADM) barcodes accepted</p>
            </CardContent>
          )}
        </Card>

        {!pendingStudent && !lastScan && (
          <div className="space-y-3">
            {!scanVisibility.isVisible && (
              <Card className="border-destructive/30 bg-destructive/5">
                <CardContent className="flex items-center gap-3 py-3">
                  <ShieldAlert className="w-5 h-5 text-destructive shrink-0" />
                  <p className="text-sm text-destructive">{scanVisibility.reason}</p>
                </CardContent>
              </Card>
            )}
            <div className="flex gap-3">
              {!scanning ? (
                <Button onClick={startScanner} className="flex-1" size="lg" disabled={!scanVisibility.isVisible}><Camera className="w-5 h-5 mr-2" />Start Scanning</Button>
              ) : (
                <Button onClick={stopScanner} variant="destructive" className="flex-1" size="lg"><CameraOff className="w-5 h-5 mr-2" />Stop Scanner</Button>
              )}
            </div>
          </div>
        )}

        {pendingStudent && (
          <Card className="border-primary/50 bg-primary/5">
            <CardContent className="py-6 space-y-4">
              <div className="flex flex-col items-center text-center gap-3">
                <Avatar className="h-24 w-24">
                  {pendingStudent.photoUrl && <AvatarImage src={pendingStudent.photoUrl} alt={pendingStudent.name} />}
                  <AvatarFallback className="bg-primary/10 text-primary font-bold text-2xl">{pendingStudent.initials}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold text-xl">{pendingStudent.name}</p>
                  <p className="text-sm text-muted-foreground">{pendingStudent.program || "No program"}{pendingStudent.nta_level ? ` — NTA ${pendingStudent.nta_level}` : ""}</p>
                  <p className="text-xs font-mono text-muted-foreground mt-1">{pendingStudent.admNumber}</p>
                </div>
              </div>
              <Button className="w-full" size="lg" onClick={handleConfirm} disabled={confirming}>
                <CheckCircle2 className="w-5 h-5 mr-2" />{confirming ? "Confirming..." : `Confirm ${mode === "entry" ? "Entry" : "Exit"}`}
              </Button>
            </CardContent>
          </Card>
        )}

        {lastScan && (
          <Card className={lastScan.status === "success" ? "border-primary/50 bg-primary/5" : "border-destructive/50 bg-destructive/5"}>
            <CardContent className="py-6 space-y-4">
              <div className="flex items-center gap-4">
                {lastScan.status === "success"
                  ? <CheckCircle2 className="w-10 h-10 text-primary shrink-0" />
                  : <XCircle className="w-10 h-10 text-destructive shrink-0" />}
                <div>
                  <p className="font-semibold text-lg">{lastScan.name}</p>
                  {lastScan.admNumber && <p className="text-xs font-mono text-muted-foreground">{lastScan.admNumber}</p>}
                  <p className="text-sm text-muted-foreground">{lastScan.message}</p>
                </div>
                {lastScan.status === "success" && (
                  <Badge className="ml-auto text-base px-4 py-1">PRESENT</Badge>
                )}
              </div>
              <div className="flex gap-3">
                <Button
                  className="flex-1"
                  size="lg"
                  onClick={() => { setLastScan(null); startScanner(); }}
                >
                  <Camera className="w-5 h-5 mr-2" />Scan Next
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  size="lg"
                  onClick={() => { setLastScan(null); }}
                >
                  End Session
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Today's Gate Attendance Log */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {activeTask ? `Backdated Gate Log — ${format(new Date(activeTask.attendance_date + "T00:00:00"), "PPP")}` : `Main Gate Attendance Log — ${new Date().toLocaleDateString("en-GB", { timeZone: "Africa/Dar_es_Salaam" })}`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">S/N</TableHead>
                  <TableHead>Student Name</TableHead>
                  <TableHead>ADM Number</TableHead>
                  <TableHead>Time In</TableHead>
                  <TableHead className="w-16">Signature</TableHead>
                  <TableHead>Time Out</TableHead>
                  <TableHead className="w-16">Signature</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {todayLog.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No attendance records for today.</TableCell></TableRow>
                ) : (
                  todayLog.map((row, idx) => (
                    <TableRow key={row.student_id}>
                      <TableCell className="font-mono text-xs">{idx + 1}</TableCell>
                      <TableCell className="font-medium">{row.student_name}</TableCell>
                      <TableCell className="font-mono text-sm">{row.adm_number}</TableCell>
                      <TableCell className="text-sm">{formatTime(row.time_in)}</TableCell>
                      <TableCell>
                        {row.signature_url && row.time_in ? (
                          <img src={row.signature_url} alt="sig" className="h-6 w-auto max-w-[60px] object-contain" />
                        ) : "—"}
                      </TableCell>
                      <TableCell className="text-sm">{formatTime(row.time_out)}</TableCell>
                      <TableCell>
                        {row.signature_url && row.time_out ? (
                          <img src={row.signature_url} alt="sig" className="h-6 w-auto max-w-[60px] object-contain" />
                        ) : "—"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Reports Tab                                                        */
/* ------------------------------------------------------------------ */
function ReportsTab() {
  const [modules, setModules] = useState<any[]>([]);
  const [selectedModule, setSelectedModule] = useState("");
  const [report, setReport] = useState<any[]>([]);
  const [totalSessions, setTotalSessions] = useState(0);

  useEffect(() => {
    supabase.from("modules").select("module_code, module_name, class").order("module_code").then(({ data }) => setModules(data ?? []));
  }, []);

  useEffect(() => {
    if (!selectedModule) return;
    loadReport();
  }, [selectedModule]);

  async function loadReport() {
    // Get sessions for this module
    const { data: sessionData } = await supabase
      .from("attendance_sessions")
      .select("id")
      .eq("module_code", selectedModule);

    const sessionIds = sessionData?.map(s => s.id) ?? [];
    setTotalSessions(sessionIds.length);

    if (sessionIds.length === 0) { setReport([]); return; }

    // Get module's class to find students
    const mod = modules.find(m => m.module_code === selectedModule);
    if (!mod) return;

    const { data: students } = await supabase
      .from("students")
      .select("id, first_name, last_name, student_number")
      .eq("class", mod.class);

    if (!students || students.length === 0) { setReport([]); return; }

    const results = await Promise.all(
      students.map(async student => {
        const { count } = await supabase
          .from("attendance_records")
          .select("id", { count: "exact", head: true })
          .eq("student_id", student.id)
          .eq("attendance_type", "session")
          .in("session_id", sessionIds);

        const attended = count ?? 0;
        const percentage = sessionIds.length > 0 ? Math.round((attended / sessionIds.length) * 100) : 0;
        return {
          ...student,
          fullName: `${student.first_name} ${student.last_name}`,
          attended,
          totalSessions: sessionIds.length,
          percentage,
          eligible: percentage >= 80,
        };
      })
    );

    setReport(results);
  }

  return (
    <div className="space-y-6">
      <div className="max-w-xs space-y-2">
        <Label>Select Module</Label>
        <Select value={selectedModule} onValueChange={setSelectedModule}>
          <SelectTrigger><SelectValue placeholder="Choose a module" /></SelectTrigger>
          <SelectContent>
            {modules.map(m => (
              <SelectItem key={m.module_code} value={m.module_code}>{m.module_code} — {m.module_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedModule && (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Student No.</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Attended</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>%</TableHead>
                <TableHead>Eligibility</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No data available.</TableCell></TableRow>
              ) : (
                report.map(r => (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-sm">{r.student_number}</TableCell>
                    <TableCell className="font-medium">{r.fullName}</TableCell>
                    <TableCell>{r.attended}</TableCell>
                    <TableCell>{r.totalSessions}</TableCell>
                    <TableCell className="font-semibold">{r.percentage}%</TableCell>
                    <TableCell>
                      <Badge variant={r.eligible ? "default" : "destructive"}>
                        {r.eligible ? "Eligible" : "Not Eligible"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Attendance Page                                               */
/* ------------------------------------------------------------------ */
export default function Attendance() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [todayScans, setTodayScans] = useState(0);
  const [activeSessions, setActiveSessions] = useState(0);
  const [activeTask, setActiveTask] = useState<CustomTask | null>(null);
  const [pendingTasks, setPendingTasks] = useState<CustomTask[]>([]);
  const [activeTab, setActiveTab] = useState(searchParams.get("task") ? "gate" : "sessions");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newTaskDate, setNewTaskDate] = useState<Date>();
  const [newTaskNotes, setNewTaskNotes] = useState("");
  const [creating, setCreating] = useState(false);
  const { toast } = useToast();
  const { user, hasRole } = useAuth();
  const isSuperadmin = hasRole("superadmin");

  // ---- Role-based tab visibility ----
  // IT Incharge gets the same view/permissions as Superadmin
  const isManagement =
    hasRole("superadmin") ||
    hasRole("it_incharge") ||
    hasRole("principal") ||
    hasRole("deputy_principal") ||
    hasRole("head_academic") ||
    hasRole("head_administration");
  const isSecurity = hasRole("security_man");
  const CR_ROLES = [
    "cr_cmt4","cr_cmt5","cr_cmt6",
    "cr_pst4","cr_pst5","cr_pst6",
    "cr_sw4","cr_sw5","cr_sw6",
  ] as const;
  const isCR = CR_ROLES.some((r) => hasRole(r as any));
  // HOD without a privileged override role: gets department-scoped attendance access.
  const isHod = !isManagement && (hasRole("hod_cmt") || hasRole("hod_pst") || hasRole("hod_sw"));
  const isOtherStaff = false;

  const [isGroupLeader, setIsGroupLeader] = useState(false);
  useEffect(() => {
    if (!user) { setIsGroupLeader(false); return; }
    let cancelled = false;
    (async () => {
      const { data: stu } = await supabase
        .from("students").select("id").eq("user_id", user.id).maybeSingle();
      if (!stu?.id) { if (!cancelled) setIsGroupLeader(false); return; }
      const { data: grp } = await supabase
        .from("field_groups").select("id").eq("leader_student_id", stu.id).limit(1);
      if (!cancelled) setIsGroupLeader((grp?.length ?? 0) > 0);
    })();
    return () => { cancelled = true; };
  }, [user]);

  const allowedTabs = useMemo(() => {
    const s = new Set<string>();
    if (isManagement) {
      ["sessions","scanner","approvals","gate","rotation","reports","history"].forEach((t) => s.add(t));
      return s;
    }
    if (isHod) {
      // Department-scoped tabs: class/session-based + rotation-based + read-only insight.
      ["sessions","scanner","rotation","reports","history"].forEach((t) => s.add(t));
    }
    if (isSecurity) s.add("gate");
    if (isCR) s.add("scanner");
    if (isGroupLeader) s.add("rotation");
    if (isOtherStaff && s.size === 0) {
      ["sessions","reports","history"].forEach((t) => s.add(t));
    }
    if (s.size === 0) s.add("sessions");
    return s;
  }, [isManagement, isHod, isSecurity, isCR, isGroupLeader, isOtherStaff]);

  const defaultTabForRole = useMemo(() => {
    if (isManagement) return searchParams.get("task") ? "gate" : "sessions";
    if (isHod) return "sessions";
    if (isSecurity) return "gate";
    if (isCR) return "scanner";
    if (isGroupLeader) return "rotation";
    return Array.from(allowedTabs)[0] ?? "sessions";
  }, [isManagement, isHod, isSecurity, isCR, isGroupLeader, allowedTabs, searchParams]);

  useEffect(() => {
    if (!allowedTabs.has(activeTab)) setActiveTab(defaultTabForRole);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedTabs, defaultTabForRole]);

  const loadTasks = useCallback(async () => {
    // Load task from URL param
    const taskId = searchParams.get("task");
    if (taskId) {
      const { data } = await supabase
        .from("custom_attendance_tasks")
        .select("*")
        .eq("id", taskId)
        .maybeSingle();
      if (data && data.status !== "completed") {
        setActiveTask(data as CustomTask);
        // Mark as in_progress
        if (data.status === "pending") {
          await supabase.from("custom_attendance_tasks").update({ status: "in_progress" }).eq("id", taskId);
        }
      }
    }
    // Load all pending tasks for display
    const { data: tasks } = await supabase
      .from("custom_attendance_tasks")
      .select("*")
      .in("status", ["pending", "in_progress"])
      .order("attendance_date", { ascending: false });
    setPendingTasks((tasks ?? []) as CustomTask[]);
  }, [searchParams]);

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    Promise.all([
      supabase.from("attendance_records").select("student_id").eq("attendance_date", today),
      supabase.from("attendance_sessions").select("id", { count: "exact", head: true }).eq("status", "open"),
    ]).then(([scans, sessions]) => {
      const uniqueStudents = new Set((scans.data ?? []).map((r: any) => r.student_id)).size;
      setTodayScans(uniqueStudents);
      setActiveSessions(sessions.count ?? 0);
    });
    loadTasks();
  }, [loadTasks]);

  async function handleCreateTask() {
    if (!newTaskDate || !user) return;
    setCreating(true);
    const dateStr = format(newTaskDate, "yyyy-MM-dd");

    const { data: task, error } = await supabase
      .from("custom_attendance_tasks")
      .insert({
        attendance_date: dateStr,
        created_by: user.id,
        notes: newTaskNotes.trim() || null,
      })
      .select("id")
      .single();

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      setCreating(false);
      return;
    }

    // Send notification to all security_man users
    const { data: securityUserIds } = await supabase.rpc("get_users_by_roles", { _roles: ["security_man"] });
    if (securityUserIds && securityUserIds.length > 0) {
      const link = `/attendance?task=${task.id}`;
      await supabase.rpc("send_bulk_notifications", {
        _user_ids: securityUserIds,
        _title: `Custom Attendance: ${format(newTaskDate, "PPP")}`,
        _message: newTaskNotes.trim() || "A backdated attendance task has been assigned to you.",
        _type: "info",
        _link: link,
      });
    }

    toast({ title: "Task created", description: `Backdated attendance for ${format(newTaskDate, "PPP")} assigned to security staff.` });
    setCreateDialogOpen(false);
    setNewTaskDate(undefined);
    setNewTaskNotes("");
    setCreating(false);
    loadTasks();
  }

  function handleTaskComplete() {
    setActiveTask(null);
    setSearchParams({});
    loadTasks();
    toast({ title: "Task completed" });
  }

  function selectTask(task: CustomTask) {
    setSearchParams({ task: task.id });
    setActiveTask(task);
    setActiveTab("gate");
  }

  return (
    <AppLayout>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Attendance</h1>
          <p className="text-muted-foreground">Track student attendance for sessions and campus gate</p>
        </div>
        {(isSuperadmin || hasRole("it_incharge")) && (
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline">
                <CalendarIcon className="w-4 h-4 mr-1" /> Custom Attendance
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create Backdated Attendance Task</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Select Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn("w-full justify-start text-left font-normal", !newTaskDate && "text-muted-foreground")}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {newTaskDate ? format(newTaskDate, "PPP") : "Pick a past date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={newTaskDate}
                        onSelect={setNewTaskDate}
                        disabled={(date) => date >= new Date() || date < new Date("2024-01-01")}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Notes (optional)</Label>
                  <Textarea
                    placeholder="e.g. Gate scanner was offline on this day"
                    value={newTaskNotes}
                    onChange={e => setNewTaskNotes(e.target.value)}
                    rows={3}
                  />
                </div>
                <Button className="w-full" onClick={handleCreateTask} disabled={!newTaskDate || creating}>
                  {creating ? "Creating..." : "Create & Notify Security"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Today's Students</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold flex items-center gap-2"><Users className="h-5 w-5 text-primary" />{todayScans}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Active Sessions</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold flex items-center gap-2"><CalendarCheck className="h-5 w-5 text-primary" />{activeSessions}</div></CardContent>
        </Card>
      </div>

      {/* Pending custom attendance tasks — Gate-Scanner workflow only */}
      {allowedTabs.has("gate") && pendingTasks.length > 0 && (
        <div className="mb-6 space-y-2">
          <h3 className="text-sm font-semibold flex items-center gap-1"><ClipboardList className="w-4 h-4" /> Pending Attendance Tasks</h3>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {pendingTasks.map(t => (
              <Card
                key={t.id}
                className={cn(
                  "cursor-pointer hover:border-primary/50 transition-colors",
                  activeTask?.id === t.id && "border-primary ring-1 ring-primary"
                )}
                onClick={() => selectTask(t)}
              >
                <CardContent className="py-3 flex items-center gap-3">
                  <CalendarIcon className="w-5 h-5 text-primary shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{format(new Date(t.attendance_date + "T00:00:00"), "PPP")}</p>
                    <p className="text-xs text-muted-foreground truncate">{t.notes || "No notes"}</p>
                  </div>
                  <Badge variant={t.status === "in_progress" ? "default" : "secondary"} className="ml-auto shrink-0">
                    {t.status === "in_progress" ? "In Progress" : "Pending"}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4 flex flex-wrap h-auto">
          {allowedTabs.has("sessions") && <TabsTrigger value="sessions"><CalendarCheck className="w-4 h-4 mr-1" />Sessions</TabsTrigger>}
          {allowedTabs.has("scanner") && <TabsTrigger value="scanner"><ScanLine className="w-4 h-4 mr-1" />Session Scanner</TabsTrigger>}
          {allowedTabs.has("approvals") && <TabsTrigger value="approvals"><CheckCircle2 className="w-4 h-4 mr-1" />Pending Approvals</TabsTrigger>}
          {allowedTabs.has("gate") && <TabsTrigger value="gate"><DoorOpen className="w-4 h-4 mr-1" />Gate Scanner</TabsTrigger>}
          {allowedTabs.has("rotation") && <TabsTrigger value="rotation"><MapPin className="w-4 h-4 mr-1" />Rotation</TabsTrigger>}
          {allowedTabs.has("reports") && <TabsTrigger value="reports"><BarChart3 className="w-4 h-4 mr-1" />Reports</TabsTrigger>}
          {allowedTabs.has("history") && <TabsTrigger value="history"><ClipboardList className="w-4 h-4 mr-1" />History</TabsTrigger>}
        </TabsList>
        {allowedTabs.has("sessions") && <TabsContent value="sessions"><SessionsTab /></TabsContent>}
        {allowedTabs.has("scanner") && <TabsContent value="scanner"><ScannerTab /></TabsContent>}
        {allowedTabs.has("approvals") && <TabsContent value="approvals"><PendingApprovalsQueue /></TabsContent>}
        {allowedTabs.has("gate") && <TabsContent value="gate"><GateTab activeTask={activeTask} onTaskComplete={handleTaskComplete} /></TabsContent>}
        {allowedTabs.has("rotation") && <TabsContent value="rotation"><RotationTab /></TabsContent>}
        {allowedTabs.has("reports") && <TabsContent value="reports"><AttendanceReports /></TabsContent>}
        {allowedTabs.has("history") && <TabsContent value="history"><SessionHistoryView /></TabsContent>}
      </Tabs>
    </AppLayout>
  );
}
